import { supabase } from "@/integrations/supabase/client";
import { isMissingFunctionError } from "@/utils/postgrestErrors";
import { queryClient } from "@/lib/queryClient";
import {
  createResyncChannel,
  realtimeTopic,
  type ResyncChannelHandle,
} from "@/lib/realtime/channel";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  IssueStatus,
  PMIssue,
  PMIssueChecklistItem,
  PMIssueComment,
  PMIssueActivity,
  PMActivityActionType,
  Discipline,
  PMIssueFilters,
  UserProfile,
  LinkedDocument,
  LinkedElement,
  ProtocolData,
  IssueViewpoint,
  DrawingIssueAnchor,
  PMIssueStatus,
  PMIssueDependency,
  IssueDependencyType,
  IssueDependencyHardness,
  IssueStatusCategory,
  SchedulePreview,
  ScheduleDateChange,
  ScheduleEdgeChanges,
  ScheduleSnapshot,
  DocumentIssueRoot,
} from "@/domains/pm/types";
import { issueDisplayTitle } from "@/domains/pm/issueTitle";
import type { IssuePlacementRow, PlacementLayout } from "@/domains/pm/utils/issuePlacement";
import { resolveDocumentBreadcrumb } from "@/domains/documents/utils/folderPathResolver";

/**
 * Bind a PostgREST request to a caller's AbortSignal when there is one, so a
 * long fan-out (the protocol export) can be cancelled mid-flight. No
 * signal leaves the query exactly as it was.
 */
function withSignal<T extends { abortSignal(signal: AbortSignal): T }>(
  query: T,
  signal?: AbortSignal
): T {
  return signal ? query.abortSignal(signal) : query;
}

/**
 * A user's search term as a PostgREST `ilike` value: the term verbatim,
 * wrapped in wildcards.
 *
 * It must NOT be double-quoted. PostgREST parses a single filter's value as
 * `pSingleVal = many anyChar` (`ApiRequest/QueryParams.hs`) — every remaining
 * character is taken literally and nothing is unquoted, so `,` `.` `:` `()`
 * need no escaping here and surrounding quotes would become part of the
 * pattern, making every search match zero rows. Quoting is only meaningful
 * inside `in.(…)` lists (`pListElement`) and `or=`/`and=` trees
 * (`pLogicSingleVal`), which the search term never reaches — all three call
 * sites pass it to a plain `.ilike()`.
 *
 * `%` stays a wildcard on purpose — it is how the term is made a substring
 * match, and a term containing one behaves like the wildcard search the user
 * typed.
 */
export function ilikeSearchValue(search: string): string {
  return `%${search}%`;
}

// ---------------------------------------------------------------------------
// Document breadcrumbs (WHA-3457)
// ---------------------------------------------------------------------------

/**
 * Rows of a lookup table for a set of ids, keyed by id — a join's second hop.
 *
 * The id list is chunked for the same reason the junction hop below is paged:
 * PostgREST truncates at max-rows (1000) with no error, so a bare
 * `.in("id", ids)` over a wide fan-out silently loses its tail — and a profile
 * missing from the map is an issue printed as "Unassigned" in a document whose
 * whole design is to declare what it could not read. Ids are unique, so a chunk
 * under the cap can never come back short; it keeps the request URL sane too.
 */
const ID_LOOKUP_CHUNK_SIZE = 500;

async function fetchRowsByIds<T extends { id: number }>(
  table: "user_profiles" | "documents",
  columns: string,
  ids: number[],
  signal?: AbortSignal
): Promise<Map<number, T>> {
  const rows = new Map<number, T>();
  for (let start = 0; start < ids.length; start += ID_LOOKUP_CHUNK_SIZE) {
    const { data, error } = await withSignal(
      supabase
        .from(table)
        .select(columns)
        .in("id", ids.slice(start, start + ID_LOOKUP_CHUNK_SIZE)),
      signal
    );
    if (error) throw new Error(error.message);
    for (const row of (data ?? []) as T[]) rows.set(row.id, row);
  }
  return rows;
}

/**
 * Rows of an issue junction table for a set of issues, read whole.
 *
 * PostgREST answers with at most the project's max-rows (1000) and drops the
 * rest without an error, so a bare `.in("issue_id", ids)` silently loses its
 * tail once the fan-out is big enough — the protocol export asks for
 * 200 issues at a time, and 200 issues with a handful of assignees each is
 * already within reach of that cap. The pages are keyset, not offset, so a
 * link written while they are being read cannot shift a row past the cursor.
 */
const ISSUE_LINK_PAGE_SIZE = 500;

/** Issue-scoped tables whose primary key starts with `issue_id`, so a keyset
 *  on `(issue_id, tiebreak)` walks them in a total order. */
type IssueScopedTable =
  | "pm_issue_assignees"
  | "pm_issue_disciplines"
  | "pm_issue_documents"
  | "pm_issue_elements"
  | "pm_issue_comments"
  | "pm_issue_checklist_items";

/**
 * An Error that keeps PostgREST's `code` and `hint`. A bare `new Error(message)`
 * throws them away, and they are what tells an undeployed table (42P01 /
 * PGRST205) or a stale checklist reorder (`pm_checklist_order_stale`) apart
 * from an ordinary refusal.
 */
interface PostgrestFailureDetails {
  message: string;
  code?: string | null;
  hint?: string | null;
}
type RepositoryError = Error & { code?: string; hint?: string };

function pgError(error: PostgrestFailureDetails): RepositoryError {
  return Object.assign(new Error(error.message), {
    code: error.code ?? undefined,
    hint: error.hint ?? undefined,
  });
}

/**
 * Every row of an issue-scoped table for a set of issues, read whole.
 *
 * Generalises the junction read above to tables that carry more than the link
 * column — `pm_issue_elements` denormalises the element's guid/name/type,
 * `pm_issue_comments` is read for its ids. `tiebreak` must be the second half
 * of the table's primary key so `(issue_id, tiebreak)` is unique.
 */
async function fetchIssueScopedRows<T extends { issue_id: number }>(
  table: IssueScopedTable,
  columns: string,
  tiebreak: string,
  issueIds: number[],
  signal?: AbortSignal
): Promise<T[]> {
  if (issueIds.length === 0) return [];
  const rows: T[] = [];
  let cursor: T | undefined;
  for (;;) {
    let query = supabase
      .from(table)
      .select(columns)
      .in("issue_id", issueIds)
      .order("issue_id", { ascending: true })
      .order(tiebreak, { ascending: true })
      .limit(ISSUE_LINK_PAGE_SIZE);
    if (cursor) {
      const tie = (cursor as unknown as Record<string, number>)[tiebreak];
      query = query.or(
        `issue_id.gt.${cursor.issue_id},and(issue_id.eq.${cursor.issue_id},${tiebreak}.gt.${tie})`
      );
    }
    const { data, error } = await withSignal(query, signal);
    if (error) throw pgError(error);
    const page = (data ?? []) as unknown as T[];
    rows.push(...page);
    // A short page is the end of the set: the page size stays under max-rows,
    // so a full page is our limit talking, never the server's.
    if (page.length < ISSUE_LINK_PAGE_SIZE) return rows;
    cursor = page[page.length - 1];
  }
}

function fetchIssueLinks<K extends string>(
  table: "pm_issue_assignees" | "pm_issue_disciplines" | "pm_issue_documents",
  linkColumn: K,
  issueIds: number[],
  signal?: AbortSignal
): Promise<Array<{ issue_id: number } & Record<K, number>>> {
  return fetchIssueScopedRows<{ issue_id: number } & Record<K, number>>(
    table,
    `issue_id, ${linkColumn}`,
    linkColumn,
    issueIds,
    signal
  );
}

/**
 * The comment ids, among `commentIds`, carrying an unread @-mention of
 * `userId` — one entry per unread mention, so the caller can count them.
 *
 * The id list is chunked (its own URL would otherwise grow without bound) and
 * each chunk keyset-paged on comment_id, unique here because the primary key
 * is (comment_id, mentioned_user_id) and the user is pinned. Without the
 * paging a busy building's unread badges would stop at PostgREST's max-rows.
 */
async function fetchUnreadMentionCommentIds(
  commentIds: number[],
  userId: number
): Promise<number[]> {
  const found: number[] = [];
  for (let start = 0; start < commentIds.length; start += ID_LOOKUP_CHUNK_SIZE) {
    const chunk = commentIds.slice(start, start + ID_LOOKUP_CHUNK_SIZE);
    let cursor: number | null = null;
    for (;;) {
      let query = supabase
        .from("pm_issue_comment_mentions")
        .select("comment_id")
        .in("comment_id", chunk)
        .eq("mentioned_user_id", userId)
        .is("read_at", null)
        .order("comment_id", { ascending: true })
        .limit(ISSUE_LINK_PAGE_SIZE);
      if (cursor !== null) query = query.gt("comment_id", cursor);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      const page = (data ?? []) as Array<{ comment_id: number }>;
      for (const row of page) found.push(row.comment_id);
      if (page.length < ISSUE_LINK_PAGE_SIZE) break;
      cursor = page[page.length - 1].comment_id;
    }
  }
  return found;
}

/**
 * Batch-resolve assignees for a set of issues via pm_issue_assignees →
 * user_profiles. Returns a map of issue id → assignee profiles (issues with no
 * assignees are simply absent). Shared by listIssues (full PMIssue hydration)
 * and the viewer's LinkedIssueSummary fetchers (WHA-3621) so the join logic
 * lives in one place.
 */
async function hydrateAssignees(
  issueIds: number[],
  signal?: AbortSignal
): Promise<Map<number, UserProfile[]>> {
  const result = new Map<number, UserProfile[]>();
  if (issueIds.length === 0) return result;

  const links = await fetchIssueLinks("pm_issue_assignees", "user_id", issueIds, signal);

  const userMap = await fetchRowsByIds<UserProfile>(
    "user_profiles",
    "id, first_name, last_name, email, avatar_url, color",
    Array.from(new Set(links.map((link) => link.user_id))),
    signal
  );
  for (const link of links) {
    const user = userMap.get(link.user_id);
    if (!user) continue;
    const assignees = result.get(link.issue_id) ?? [];
    assignees.push(user);
    result.set(link.issue_id, assignees);
  }
  return result;
}

/** The design_disciplines columns a `Discipline` carries. */
const DISCIPLINE_COLUMNS =
  "id, name, name_en, abbreviation, abbreviation_en, description, bg_color, text_color, group_code, group_code_en";
/** The same, before franklin-data-pipeline's `discipline_english_abbreviations`
 *  migration adds the `_en` codes. */
const PRE_ENGLISH_DISCIPLINE_COLUMNS =
  "id, name, name_en, abbreviation, description, bg_color, text_color, group_code";

/**
 * design_disciplines rows — every discipline A–Z by name, or those with `ids`.
 *
 * Until the English-codes migration applies, PostgREST refuses the `_en`
 * columns with 42703 and would take every discipline pill down with them.
 * That schema is detected and read without them instead, so the pills fall
 * back to the Estonian codes rather than disappearing.
 */
async function readDisciplines(ids: number[] | null, signal?: AbortSignal): Promise<Discipline[]> {
  const read = async (columns: string) => {
    if (ids === null) {
      return withSignal(
        supabase.from("design_disciplines").select(columns).order("name", { ascending: true }),
        signal
      );
    }
    const rows: unknown[] = [];
    for (let start = 0; start < ids.length; start += ID_LOOKUP_CHUNK_SIZE) {
      const { data, error } = await withSignal(
        supabase
          .from("design_disciplines")
          .select(columns)
          .in("id", ids.slice(start, start + ID_LOOKUP_CHUNK_SIZE)),
        signal
      );
      if (error) return { data: null, error };
      rows.push(...(data ?? []));
    }
    return { data: rows, error: null };
  };

  let { data, error } = await read(DISCIPLINE_COLUMNS);
  if (error?.code === "42703") ({ data, error } = await read(PRE_ENGLISH_DISCIPLINE_COLUMNS));
  if (error) throw new Error(error.message);
  return ((data ?? []) as Discipline[]).map((row) => ({
    ...row,
    abbreviation_en: row.abbreviation_en ?? null,
    group_code_en: row.group_code_en ?? null,
  }));
}

/**
 * Batch-resolve disciplines for a set of issues via pm_issue_disciplines →
 * design_disciplines. Returns a map of issue id → disciplines (issues with no
 * discipline are simply absent). Shared by listIssues and the protocol
 * fetch, which needs the same join without the rest of listIssues' hydration.
 */
async function hydrateDisciplines(
  issueIds: number[],
  signal?: AbortSignal
): Promise<Map<number, Discipline[]>> {
  const result = new Map<number, Discipline[]>();
  if (issueIds.length === 0) return result;

  const links = await fetchIssueLinks("pm_issue_disciplines", "discipline_id", issueIds, signal);

  const disciplines = await readDisciplines(
    Array.from(new Set(links.map((link) => link.discipline_id))),
    signal
  );
  const disciplineMap = new Map(disciplines.map((d) => [d.id, d]));
  for (const link of links) {
    const discipline = disciplineMap.get(link.discipline_id);
    if (!discipline) continue;
    const disciplines = result.get(link.issue_id) ?? [];
    disciplines.push(discipline);
    result.set(link.issue_id, disciplines);
  }
  return result;
}

/** Minimal folder shape consumed by `resolveDocumentBreadcrumb`. */
/**
 * Every read of a document linked to an issue or comment. `hidden` and
 * `chatbot_attachment` mark a file the chatbot keeps in its hidden attachments
 * project: never rendered as a path, and finished once it is readable.
 */
const LINKED_DOCUMENT_COLUMNS =
  "id, filename, state, content_type, file_path, size, created_at, parent_document_id, version, project_id, folder_id, hidden, chatbot_attachment";

type RepoFolderLike = {
  folder_path?: string | null;
  name?: string;
  project_id?: number | null;
  parentId?: string | null;
};

/**
 * WHA-3457: resolve interactive folder-path breadcrumbs for a batch of
 * linked documents and attach them as `breadcrumbSegments`, reusing the same
 * resolver the /documents page uses. Best-effort — a failure here leaves the
 * tooltip absent rather than breaking issue/comment loading. Mutates the
 * passed document objects in place.
 */
export async function attachDocumentBreadcrumbs(docs: LinkedDocument[]): Promise<void> {
  if (docs.length === 0) return;
  const projectIds = Array.from(
    new Set(docs.map((d) => d.project_id).filter((id): id is number => id != null))
  );
  if (projectIds.length === 0) return;

  const [projectRes, folderRes] = await Promise.all([
    (supabase as any).from("building_projects").select("id, name").in("id", projectIds),
    (supabase as any)
      .from("document_folders")
      .select("id, name, parent_id, folder_path, project_id")
      .in("project_id", projectIds)
      .eq("is_deleted", false),
  ]);
  if (projectRes.error || folderRes.error) return;

  const projects = new Map<string, { name: string }>(
    ((projectRes.data ?? []) as { id: number; name: string }[]).map((p) => [
      String(p.id),
      { name: p.name },
    ])
  );
  const folders = new Map<string, RepoFolderLike>(
    (
      (folderRes.data ?? []) as {
        id: number;
        name: string | null;
        parent_id: number | null;
        folder_path: string | null;
        project_id: number | null;
      }[]
    ).map((f) => [
      String(f.id),
      {
        folder_path: f.folder_path,
        name: f.name ?? undefined,
        project_id: f.project_id,
        parentId: f.parent_id != null ? String(f.parent_id) : null,
      },
    ])
  );

  for (const doc of docs) {
    // WHA-3774: a file uploaded directly to a comment/issue has no folder
    // (folder_id null) — show just the filename, no folder-path breadcrumb.
    // Only files that live in a real folder get the interactive path.
    // A hidden file lives in an internal project the Documents page filters
    // out; its segments would navigate somewhere nobody can see.
    if (doc.folder_id == null || doc.hidden) {
      doc.breadcrumbSegments = null;
      continue;
    }
    doc.breadcrumbSegments = resolveDocumentBreadcrumb(
      { project_id: doc.project_id, folder_id: doc.folder_id, filename: doc.filename },
      projects,
      folders
    );
  }
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

/**
 * WHA-3452: count non-deleted issues in the current scope — building +
 * optional project + optional search — WITHOUT the IssueFilterControl
 * dimensions (assignee / discipline / documents / comments). Subtracting
 * `listIssues`'s filtered count from this gives "N hidden by filters".
 *
 * `status` / `dueBefore` are applied here only when the CALLER passes them,
 * and what the caller passes is the point. Where they define which view the
 * user is on — My Issues' Overdue tab — they belong in the baseline too, so
 * switching tabs cannot inflate the hidden-by-filters count. Where they are
 * user-chosen filter dimensions — the Issues page, since the status tabs
 * became filters — they must be LEFT OUT, or the issues they hide would not
 * be counted as hidden. IssuesStore.setFilters takes an explicit baseline for
 * exactly this; see effectiveIssueFilters.toScopeBaselineFilters.
 */
export async function countIssuesInScope(
  buildingId: number,
  filters: Pick<PMIssueFilters, "buildingProjectId" | "search" | "status" | "dueBefore">
): Promise<number> {
  let query = supabase
    .from("pm_issues")
    .select("id", { count: "exact", head: true })
    .eq("building_id", buildingId)
    .is("deleted_at", null);
  if (filters.search && filters.search.trim()) {
    query = query.ilike("title", ilikeSearchValue(filters.search.trim()));
  }
  if (filters.buildingProjectId !== undefined) {
    query = query.eq("building_project_id", filters.buildingProjectId);
  }
  if (filters.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }
  if (filters.dueBefore) {
    query = query.lt("due_date", filters.dueBefore);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Scope-wide issue totals per status, shown against the status rows in the
 * filter control.
 *
 * Scoped to project + search ONLY — deliberately not to the other filter
 * dimensions, and never to the status selection itself. A count that narrowed
 * with the selection would change as the user ticked boxes, so "Resolved 12"
 * would become "Resolved 12 of the ones you already filtered to", which is not
 * the question the row answers. Mirrors countOverdueIssues.
 *
 * These used to be derived from the loaded page. That stopped being possible
 * when status became a server-side filter: a page narrowed to one status can
 * say nothing about the others (the old code carried a frozen-counts ref to
 * paper over exactly this for the Overdue tab).
 *
 * PostgREST has no GROUP BY, so this is one head-only count per status run in
 * parallel — no rows are transferred.
 */
export async function countIssuesByStatus(
  buildingId: number,
  statuses: IssueStatus[],
  buildingProjectId?: number,
  search?: string
): Promise<Record<IssueStatus, number>> {
  const countOne = async (status: IssueStatus): Promise<number> => {
    let query = supabase
      .from("pm_issues")
      .select("id", { count: "exact", head: true })
      .eq("building_id", buildingId)
      .eq("status", status)
      .is("deleted_at", null);
    if (buildingProjectId !== undefined) {
      query = query.eq("building_project_id", buildingProjectId);
    }
    if (search && search.trim()) {
      query = query.ilike("title", ilikeSearchValue(search.trim()));
    }
    const { count, error } = await query;
    if (error) throw new Error(error.message);
    return count ?? 0;
  };

  // One count per status the caller asked about, rather than a fixed three:
  // a project can define its own statuses, and a picker showing no number
  // beside one reads as "none", not as "not counted".
  const counts = await Promise.all(statuses.map((s) => countOne(s)));
  const out: Record<IssueStatus, number> = {};
  statuses.forEach((status, i) => {
    out[status] = counts[i];
  });
  return out;
}

/**
 * Count non-deleted overdue issues — an OPEN status with a due_date strictly
 * before the local-today cutoff (see overdueDueBefore) — in a building,
 * optionally narrowed to one project. Feeds the Issues page's Overdue tab
 * badge, which must count the full scope rather than the loaded page.
 */
export async function countOverdueIssues(
  buildingId: number,
  dueBefore: string,
  buildingProjectId?: number,
  openStatuses: IssueStatus[] = ["in_progress"]
): Promise<number> {
  let query = supabase
    .from("pm_issues")
    .select("id", { count: "exact", head: true })
    .eq("building_id", buildingId)
    .in("status", openStatuses)
    .lt("due_date", dueBefore)
    .is("deleted_at", null);
  if (buildingProjectId !== undefined) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Count non-deleted issues created in `buildingId` at or after `sinceIso`,
 * optionally narrowed to one project. Feeds the chat's "new issues" nudge —
 * a real count, never a placeholder.
 */
export async function countIssuesCreatedSince(
  buildingId: number,
  sinceIso: string,
  buildingProjectId?: number
): Promise<number> {
  let query = supabase
    .from("pm_issues")
    .select("id", { count: "exact", head: true })
    .eq("building_id", buildingId)
    .gte("created_at", sinceIso)
    .is("deleted_at", null);
  if (buildingProjectId !== undefined) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * WHA-3644: resolve the set of issue ids in `buildingId` where `userId` is
 * @mentioned in any comment (read or unread). Building-scoped via an inner
 * join on pm_issues. Shared by listIssues (Mentions filter) and
 * countIssuesMatching (the My Issues scope count).
 */
async function resolveMentionedIssueIds(buildingId: number, userId: number): Promise<number[]> {
  // Read the mentions directly, narrowed to this user and — through the
  // two-level inner join — to this building. The older shape pulled every
  // comment in the building back first just to map comment ids to issues;
  // both of its reads were unbounded, so on a busy building PostgREST's
  // max-rows cap silently truncated them and the Mentions tab quietly lost
  // issues. Keyset-paged on comment_id, which is unique once the user is
  // pinned (the primary key is (comment_id, mentioned_user_id)).
  const issueIds = new Set<number>();
  let cursor: number | null = null;
  for (;;) {
    let query = supabase
      .from("pm_issue_comment_mentions")
      .select("comment_id, pm_issue_comments!inner(issue_id, pm_issues!inner(building_id))")
      .eq("mentioned_user_id", userId)
      .eq("pm_issue_comments.pm_issues.building_id", buildingId)
      .order("comment_id", { ascending: true })
      .limit(ISSUE_LINK_PAGE_SIZE);
    if (cursor !== null) query = query.gt("comment_id", cursor);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    const page = (data ?? []) as unknown as Array<{
      comment_id: number;
      pm_issue_comments: { issue_id: number } | null;
    }>;
    for (const row of page) {
      const issueId = row.pm_issue_comments?.issue_id;
      if (issueId != null) issueIds.add(issueId);
    }
    if (page.length < ISSUE_LINK_PAGE_SIZE) return Array.from(issueIds);
    cursor = page[page.length - 1].comment_id;
  }
}

/**
 * WHA-3644: count non-deleted issues in `buildingId` matching just the
 * "person" dimension of the My Issues tabs (assignee / created_by /
 * mentioned), ignoring project / search / discipline filters. Used as the
 * scope baseline so the page can report "N issues hidden by filters" — i.e.
 * how many of the user's issues the active project/filter selection hides.
 *
 * The Overdue tab's own narrowing (`status` + `dueBefore`) is part of the tab,
 * not a user filter, so it is applied here as well — otherwise every issue the
 * tab itself excludes would be counted as "hidden by filters".
 */
export async function countIssuesMatching(
  buildingId: number,
  filters: Pick<
    PMIssueFilters,
    "assigneeUserIds" | "createdByUserIds" | "mentionedUserId" | "status" | "dueBefore"
  >,
  buildingProjectId?: number
): Promise<number> {
  let matchingIssueIds: number[] | null = null;
  const intersect = (next: number[]) => {
    matchingIssueIds =
      matchingIssueIds === null ? next : matchingIssueIds.filter((id) => next.includes(id));
  };

  if (filters.mentionedUserId) {
    intersect(await resolveMentionedIssueIds(buildingId, filters.mentionedUserId));
  }
  if (matchingIssueIds !== null && matchingIssueIds.length === 0) return 0;

  // The assignee dimension rides an aliased inner embed — a semi-join that
  // narrows the count without an id list, exactly as listIssues does it, so
  // the baseline and the list can never disagree about who is assigned what.
  const byAssignee = !!filters.assigneeUserIds?.length;
  let query = (supabase as any)
    .from("pm_issues")
    .select(byAssignee ? "id, issue_assignees:pm_issue_assignees!inner(user_id)" : "id", {
      count: "exact",
      head: true,
    })
    .eq("building_id", buildingId)
    .is("deleted_at", null);
  if (byAssignee) {
    query = query.in("issue_assignees.user_id", filters.assigneeUserIds);
  }
  if (buildingProjectId !== undefined) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  if (filters.createdByUserIds && filters.createdByUserIds.length > 0) {
    query = query.in("created_by", filters.createdByUserIds);
  }
  if (filters.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }
  if (filters.dueBefore) {
    query = query.lt("due_date", filters.dueBefore);
  }
  if (matchingIssueIds !== null) {
    query = query.in("id", matchingIssueIds);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Open (`in_progress`, non-deleted) issue counts per project for one building —
 * feeds the building dashboard's project-card activity line. Same definition of
 * "open" as listMyOpenAssignedIssues: resolved and closed issues are done, so
 * only `in_progress` counts as active work.
 */
export async function countOpenIssuesByProject(buildingId: number): Promise<Map<number, number>> {
  const { data, error } = await supabase
    .from("pm_issues")
    .select("building_project_id")
    .eq("building_id", buildingId)
    .eq("status", "in_progress")
    .is("deleted_at", null);
  if (error) throw new Error(error.message);

  const counts = new Map<number, number>();
  for (const row of data ?? []) {
    if (row.building_project_id == null) continue;
    counts.set(row.building_project_id, (counts.get(row.building_project_id) ?? 0) + 1);
  }
  return counts;
}

/** Lean issue shape for the project dashboard's "My tasks" widget. */
export type MyAssignedIssue = Pick<
  PMIssue,
  "id" | "issue_number" | "title" | "status" | "due_date" | "building_project_id"
> & {
  /** Linked documents and comments, counted as the Issues list counts them. */
  documents_count: number;
  comments_count: number;
};

/**
 * The current user's live, non-deleted issues in one project — or across the
 * whole building when `buildingProjectId` is null ("All projects" scope) — for
 * the project dashboard's "My tasks" widget. Finished issues are not tasks
 * anymore. Sorted by due date ascending (overdue first, undated last).
 *
 * "Live" is `openStatuses`, from the status catalogue: a project's own
 * "Blocked" is work the user still owns, and pinning this to the literal
 * in_progress would drop it off their task list entirely.
 */
/** The number in a PostgREST `relation(count)` embed: `[{ count: n }]`. */
function embeddedCount(embed: unknown): number {
  const first = Array.isArray(embed) ? (embed[0] as { count?: unknown } | undefined) : undefined;
  return typeof first?.count === "number" ? first.count : 0;
}

export async function listMyOpenAssignedIssues(
  buildingId: number,
  buildingProjectId: number | null,
  userId: number,
  openStatuses: IssueStatus[] = ["in_progress"]
): Promise<MyAssignedIssue[]> {
  // The assignee junction is resolved via an inner join (same trick as
  // listIssues' building-scoped junction queries) rather than a separate
  // global `pm_issue_assignees` fetch — otherwise a user assigned across many
  // buildings pulls every one of their issue ids into memory just to discard
  // all but this building's.
  let query = supabase
    .from("pm_issues")
    .select(
      "id, issue_number, title, status, due_date, building_project_id, pm_issue_assignees!inner(user_id), pm_issue_documents(count), pm_issue_comments(count)"
    )
    .eq("pm_issue_assignees.user_id", userId)
    .eq("building_id", buildingId)
    .in("status", openStatuses)
    .is("deleted_at", null)
    .is("pm_issue_comments.deleted_at", null)
    .order("due_date", { ascending: true, nullsFirst: false });
  if (buildingProjectId != null) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  // The embedded assignee rows only exist to drive the join — drop them. The
  // two counts arrive as `[{ count }]` embeds (one aggregate row each).
  return (data ?? []).map(
    ({
      pm_issue_assignees: _assignees,
      pm_issue_documents: documents,
      pm_issue_comments: comments,
      ...row
    }) => ({
      ...row,
      status: row.status as PMIssue["status"],
      documents_count: embeddedCount(documents),
      comments_count: embeddedCount(comments),
    })
  );
}

/**
 * One page of a building's issues, newest issue_number first.
 *
 * Keyset, not offset. The list is ordered by `issue_number` descending and
 * `(building_id, issue_number)` is unique, so `cursor` — the last issue_number
 * of the previous page — walks the whole scope in a total order. An offset
 * would not: a colleague filing an issue mid-scroll takes the highest
 * issue_number, lands at offset 0 and shifts every later row down one, so the
 * next page repeats a row and skips another.
 *
 * `count` is the exact size of the filtered scope, and is only asked for on
 * the first page (`cursor` omitted) — later pages return the count they were
 * given so the caller can carry it forward.
 */
// ---------------------------------------------------------------------------
// Document parent issues (document_issue_roots)
// ---------------------------------------------------------------------------

/**
 * The `document_issue_roots` rows for a set of issues, keyed by issue id.
 *
 * Read separately rather than embedded in the issues select so that, before
 * the pipeline migration that creates the table has applied, the issues list
 * still loads — the embed would fail the whole query. Chunked like every other
 * `.in()` over a wide set (PostgREST stops at max-rows silently).
 */
async function fetchDocumentIssueRoots(
  issueIds: number[]
): Promise<Map<number, DocumentIssueRoot>> {
  const roots = new Map<number, DocumentIssueRoot>();
  for (let start = 0; start < issueIds.length; start += ID_LOOKUP_CHUNK_SIZE) {
    const { data, error } = await (supabase as any)
      .from("document_issue_roots")
      .select("issue_id, filename, auto_title")
      .in("issue_id", issueIds.slice(start, start + ID_LOOKUP_CHUNK_SIZE));
    if (error) {
      // Table (or its newer columns) not there yet: every issue is a plain issue.
      if (isMissingRelation(error) || error.code === "42703") return roots;
      throw new Error(error.message);
    }
    for (const row of (data ?? []) as Array<{
      issue_id: number;
      filename: string;
      auto_title: boolean;
    }>) {
      roots.set(row.issue_id, { filename: row.filename, auto_title: row.auto_title });
    }
  }
  return roots;
}

/**
 * The parent issue that bundles a document's comment threads and the issues
 * raised from its viewer, created on first use. `null` when the document has
 * no project or sits in another project than the issue — and before the
 * pipeline migration that adds the RPC has applied.
 */
export async function ensureDocumentIssueRoot(
  documentId: number,
  projectId: number
): Promise<number | null> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "document_issue_root_ensure",
    { p_document_id: documentId, p_project_id: projectId }
  );
  if (error) {
    if (isMissingFunctionError(error)) return null;
    throw new Error(error.message);
  }
  return typeof data === "number" ? data : null;
}

/**
 * Stamp each issue with its root (or null) and swap the stored title for the
 * viewer-language display title where the root still owns it. The stored
 * title survives as `title_raw` so an untouched form field is not written back
 * as a rename.
 */
export async function applyDocumentRootTitles<T extends { id: number; title: string }>(
  issues: Array<T & { document_root?: DocumentIssueRoot | null; title_raw?: string }>
): Promise<void> {
  if (issues.length === 0) return;
  const roots = await fetchDocumentIssueRoots(Array.from(new Set(issues.map((i) => i.id))));
  for (const issue of issues) {
    const root = roots.get(issue.id) ?? null;
    issue.document_root = root;
    issue.title_raw = issue.title;
    issue.title = issueDisplayTitle(issue.title, root);
  }
}

export async function listIssues(
  buildingId: number,
  filters: PMIssueFilters,
  page: {
    /** Exclusive upper bound on issue_number; omit for the first page. */
    cursor?: number | null;
    limit: number;
  },
  // WHA-3339: needed to populate per-issue unread mention counts. Pass 0
  // (or any falsy value) to skip the unread aggregation.
  currentUserId = 0
): Promise<{ data: PMIssue[]; count: number | null }> {
  // Step 1: Resolve discipline + assignee filters via their junction tables.
  // Each filter narrows the set of allowed issue ids; we intersect them.
  let matchingIssueIds: number[] | null = null;

  const intersect = (next: number[]) => {
    matchingIssueIds =
      matchingIssueIds === null ? next : matchingIssueIds.filter((id) => next.includes(id));
  };

  // WHA-3644 (My Issues / Mentions tab): narrow to issues where the current
  // user is @mentioned in any comment (read OR unread). This is the one
  // junction dimension still resolved into an id list — a semi-join two
  // levels down has no embed form — and it stays small: one person's mentions
  // in one building. Routed through intersect() so a user with zero mentions
  // yields an empty result via the early-return below.
  if (filters.mentionedUserId) {
    intersect(await resolveMentionedIssueIds(buildingId, filters.mentionedUserId));
  }

  if (matchingIssueIds !== null && matchingIssueIds.length === 0) {
    return { data: [], count: page.cursor == null ? 0 : null };
  }

  /**
   * Step 2: build the main query.
   *
   * The junction dimensions — discipline, assignee, and the linked-documents
   * and comments yes/no pairs — are expressed as embedded filters rather than
   * resolved into `id IN (…)` lists. Two reasons, and the second is the one
   * that bites:
   *
   *  - An id list is only as complete as the read that produced it, and
   *    PostgREST stops at max-rows without saying so. Issues went missing the
   *    moment a filter was applied.
   *  - A building with a few thousand issues turns that list into a query
   *    string too long to send, which fails outright.
   *
   * `!inner` on an aliased embed is a semi-join: it narrows the parent rows
   * without nesting duplicates or disturbing the exact count. The empty `()`
   * embed selects nothing at all and exists only to carry `is.null`, the
   * anti-join for the "none" variants.
   */
  const isFirstPage = page.cursor == null;
  const byDiscipline = !!filters.disciplineIds?.length;
  const byAssignee = !!filters.assigneeUserIds?.length;
  const embeds = [
    byDiscipline ? "issue_disciplines:pm_issue_disciplines!inner(discipline_id)" : null,
    byAssignee ? "issue_assignees:pm_issue_assignees!inner(user_id)" : null,
    filters.hasLinkedDocuments !== undefined ? "pm_issue_documents()" : null,
    filters.hasComments !== undefined ? "pm_issue_comments()" : null,
  ].filter((embed): embed is string => embed !== null);

  let query = (supabase as any)
    .from("pm_issues")
    .select(["*", ...embeds].join(", "), isFirstPage ? { count: "exact" } : undefined)
    .eq("building_id", buildingId)
    .is("deleted_at", null);
  if (!isFirstPage) {
    query = query.lt("issue_number", page.cursor);
  }

  if (byDiscipline) {
    query = query.in("issue_disciplines.discipline_id", filters.disciplineIds);
  }
  if (byAssignee) {
    query = query.in("issue_assignees.user_id", filters.assigneeUserIds);
  }
  // WHA-3423: the linked-documents / comments yes-no filters.
  if (filters.hasLinkedDocuments !== undefined) {
    query = filters.hasLinkedDocuments
      ? query.not("pm_issue_documents", "is", null)
      : query.is("pm_issue_documents", null);
  }
  if (filters.hasComments !== undefined) {
    query = filters.hasComments
      ? query.not("pm_issue_comments", "is", null)
      : query.is("pm_issue_comments", null);
  }

  if (filters.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }
  if (filters.search && filters.search.trim()) {
    query = query.ilike("title", ilikeSearchValue(filters.search.trim()));
  }
  if (filters.buildingProjectId !== undefined) {
    query = query.eq("building_project_id", filters.buildingProjectId);
  }
  // WHA-3644 (My Issues / Created tab): created_by lives directly on
  // pm_issues, so no junction is needed.
  if (filters.createdByUserIds && filters.createdByUserIds.length > 0) {
    query = query.in("created_by", filters.createdByUserIds);
  }
  // My Issues / Overdue tab: due_date strictly before the local-today cutoff
  // (see overdueDueBefore). lt() on a null due_date is false, so undated
  // issues drop out without an explicit not-null check.
  if (filters.dueBefore) {
    query = query.lt("due_date", filters.dueBefore);
  }
  if (matchingIssueIds !== null) {
    query = query.in("id", matchingIssueIds);
  }

  const { data, count, error } = await query
    .order("issue_number", { ascending: false })
    .limit(page.limit);

  if (error) throw new Error(error.message);
  // Drop the embed columns: they exist to filter, and the aliases would
  // otherwise ride along on every issue the rest of the app handles.
  const issues = ((data ?? []) as Array<Record<string, unknown>>).map(
    ({
      issue_disciplines: _disciplineLinks,
      issue_assignees: _assigneeLinks,
      pm_issue_documents: _documentLinks,
      pm_issue_comments: _commentLinks,
      ...row
    }) => row
  ) as unknown as PMIssue[];

  // Hydrate disciplines for each issue via junction table
  if (issues.length > 0) {
    const issueIds = issues.map((i) => i.id);
    const issueDisciplinesMap = await hydrateDisciplines(issueIds);

    for (const issue of issues) {
      issue.disciplines = issueDisciplinesMap.get(issue.id) ?? [];
    }

    // Hydrate assignees via pm_issue_assignees → user_profiles
    const issueAssigneesMap = await hydrateAssignees(issueIds);
    for (const issue of issues) {
      issue.assignees = issueAssigneesMap.get(issue.id) ?? [];
    }

    // Document parent issues show "Comments for <file>" in the viewer's language.
    await applyDocumentRootTitles(issues);

    // Hydrate linked documents via pm_issue_documents → documents
    const docRows = await fetchIssueLinks("pm_issue_documents", "document_id", issueIds);

    const documentIds = Array.from(new Set(docRows.map((r) => r.document_id)));
    let docMap = new Map<number, LinkedDocument>();
    if (documentIds.length > 0) {
      docMap = await fetchRowsByIds<LinkedDocument>(
        "documents",
        LINKED_DOCUMENT_COLUMNS,
        documentIds
      );
      // WHA-3457: resolve folder-path breadcrumbs for the tooltip.
      await attachDocumentBreadcrumbs(Array.from(docMap.values()));
    }

    const issueDocsMap = new Map<number, LinkedDocument[]>();
    for (const r of docRows) {
      const doc = docMap.get(r.document_id);
      if (doc) {
        let docs = issueDocsMap.get(r.issue_id);
        if (!docs) {
          docs = [];
          issueDocsMap.set(r.issue_id, docs);
        }
        docs.push(doc);
      }
    }

    for (const issue of issues) {
      issue.linked_documents = issueDocsMap.get(issue.id) ?? [];
    }

    // Hydrate linked BIM elements via pm_issue_elements (WHA-3521). The
    // junction carries denormalised guid/name/type, so no IFC-table join.
    const elementRows = await fetchIssueScopedRows<{
      issue_id: number;
      anchor_id: number;
      element_guid: string;
      element_name: string | null;
      element_type: string;
      ifc_document_id: number | null;
    }>(
      "pm_issue_elements",
      "issue_id, anchor_id, element_guid, element_name, element_type, ifc_document_id",
      "anchor_id",
      issueIds
    );

    const issueElementsMap = new Map<number, LinkedElement[]>();
    for (const r of elementRows) {
      let list = issueElementsMap.get(r.issue_id);
      if (!list) {
        list = [];
        issueElementsMap.set(r.issue_id, list);
      }
      list.push({
        anchor_id: r.anchor_id,
        element_guid: r.element_guid,
        element_name: r.element_name,
        element_type: r.element_type,
        ifc_document_id: r.ifc_document_id,
      });
    }
    for (const issue of issues) {
      issue.linked_elements = issueElementsMap.get(issue.id) ?? [];
    }

    // Checklist progress for the list row and board card badge. The table
    // ships in its own migration: until it is deployed the counts stay
    // undefined and no badge renders, rather than the whole list failing.
    try {
      const checklistRows = await fetchIssueScopedRows<{
        issue_id: number;
        id: number;
        is_done: boolean;
      }>("pm_issue_checklist_items", "id, issue_id, is_done", "id", issueIds);
      const totals = new Map<number, { total: number; done: number }>();
      for (const r of checklistRows) {
        const c = totals.get(r.issue_id) ?? { total: 0, done: 0 };
        c.total += 1;
        if (r.is_done) c.done += 1;
        totals.set(r.issue_id, c);
      }
      for (const issue of issues) {
        const c = totals.get(issue.id);
        issue.checklist_total = c?.total ?? 0;
        issue.checklist_done = c?.done ?? 0;
      }
    } catch (e) {
      if (!isMissingRelation(e as { code?: string })) throw e;
    }

    // WHA-3339: per-issue comment counts + unread-mention counts. We
    // fetch comment ids once, aggregate counts in JS, then ask the
    // mentions table for unread rows by the current user. Skipping the
    // unread pass when currentUserId is missing keeps anonymous/server
    // callers cheap.
    const commentRows = await fetchIssueScopedRows<{ issue_id: number; id: number }>(
      "pm_issue_comments",
      "id, issue_id",
      "id",
      issueIds
    );

    const commentsByIssue = new Map<number, number>();
    const commentIdToIssueId = new Map<number, number>();
    for (const r of commentRows) {
      commentsByIssue.set(r.issue_id, (commentsByIssue.get(r.issue_id) ?? 0) + 1);
      commentIdToIssueId.set(r.id, r.issue_id);
    }
    for (const issue of issues) {
      issue.comments_count = commentsByIssue.get(issue.id) ?? 0;
    }

    if (currentUserId && commentIdToIssueId.size > 0) {
      const unreadCommentIds = await fetchUnreadMentionCommentIds(
        Array.from(commentIdToIssueId.keys()),
        currentUserId
      );

      const unreadByIssue = new Map<number, number>();
      for (const commentId of unreadCommentIds) {
        const issueId = commentIdToIssueId.get(commentId);
        if (issueId != null) {
          unreadByIssue.set(issueId, (unreadByIssue.get(issueId) ?? 0) + 1);
        }
      }
      for (const issue of issues) {
        issue.unread_mention_count = unreadByIssue.get(issue.id) ?? 0;
      }
    } else {
      for (const issue of issues) {
        issue.unread_mention_count = 0;
      }
    }
  }

  return { data: issues, count: isFirstPage ? (count ?? 0) : null };
}

// WHA-3339: total unread @-mentions for the current user across one
// building. Drives the red dot on the Project Management nav icon.
export async function getUnreadMentionCountForBuilding(
  buildingId: number,
  currentUserId: number,
  buildingProjectId?: number | null
): Promise<number> {
  if (!currentUserId) return 0;
  // Step 1: comment ids in this building (via inner join on pm_issues).
  // The !inner hint forces an inner join so the building filter actually
  // narrows the rows; without it Supabase returns left-joined rows. When a
  // project scope is active, narrow further to that project's issues.
  let commentsQuery = (supabase as any)
    .from("pm_issue_comments")
    .select("id, pm_issues!inner(building_id, building_project_id)")
    .eq("pm_issues.building_id", buildingId);
  if (buildingProjectId != null) {
    commentsQuery = commentsQuery.eq("pm_issues.building_project_id", buildingProjectId);
  }
  const { data: rows, error: cErr } = await commentsQuery;
  if (cErr) throw new Error(cErr.message);
  const commentIds = (rows ?? []).map((r: { id: number }) => r.id);
  if (commentIds.length === 0) return 0;

  // Step 2: count unread mentions on those comments for this user.
  const { count, error: mErr } = await (supabase as any)
    .from("pm_issue_comment_mentions")
    .select("*", { count: "exact", head: true })
    .in("comment_id", commentIds)
    .eq("mentioned_user_id", currentUserId)
    .is("read_at", null);
  if (mErr) throw new Error(mErr.message);
  return count ?? 0;
}

// WHA-3339: mark every unread mention on an issue's comments as read
// for the current user. Called from IssuesStore.selectIssue when the
// detail panel opens.
export async function markIssueMentionsRead(issueId: number, currentUserId: number): Promise<void> {
  if (!currentUserId) return;
  const { data: comments, error: cErr } = await (supabase as any)
    .from("pm_issue_comments")
    .select("id")
    .eq("issue_id", issueId);
  if (cErr) throw new Error(cErr.message);
  const ids = (comments ?? []).map((c: { id: number }) => c.id);
  if (ids.length === 0) return;
  const { error: mErr } = await (supabase as any)
    .from("pm_issue_comment_mentions")
    .update({ read_at: new Date().toISOString() })
    .in("comment_id", ids)
    .eq("mentioned_user_id", currentUserId)
    .is("read_at", null);
  if (mErr) throw new Error(mErr.message);
}

// ---------------------------------------------------------------------------
// Issue activity log (WHA-3732 issue history + WHA-3733 notifications)
// ---------------------------------------------------------------------------

// Line *text* labels (actor name, assignee name, discipline, filename, parent
// issue number, element name) are snapshotted into each row's jsonb by the DB
// triggers — see renderActivityLine. The actor's *avatar* (current picture /
// colour), however, is hydrated live so it matches the rest of the app.

// Batch-attach actor profiles for the avatar. No FK embed exists
// (pm_issue_activity.actor_id has no declared relationship to user_profiles),
// so we mirror the listComments author-hydration pattern.
async function hydrateActivityActors(rows: PMIssueActivity[]): Promise<void> {
  const actorIds = Array.from(
    new Set(rows.map((r) => r.actor_id).filter((id): id is number => id != null))
  );
  if (actorIds.length === 0) {
    for (const r of rows) r.actor = null;
    return;
  }
  const { data: users, error } = await (supabase as any)
    .from("user_profiles")
    .select("id, first_name, last_name, email, avatar_url, color")
    .in("id", actorIds);
  if (error) throw new Error(error.message);
  const userMap = new Map<number, UserProfile>((users ?? []).map((u: UserProfile) => [u.id, u]));
  for (const r of rows) r.actor = r.actor_id == null ? null : (userMap.get(r.actor_id) ?? null);
}

// WHA-3732: full change timeline for a single issue, newest-first.
export async function getIssueActivity(issueId: number): Promise<PMIssueActivity[]> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_activity")
    .select("*")
    .eq("issue_id", issueId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as PMIssueActivity[];
  await hydrateActivityActors(rows);
  return rows;
}

// WHA-3733: paginated activity feed across one building, newest-first. RLS
// already restricts rows to projects the caller can see; the optional project
// filter narrows further. Hydrates actor + the parent issue's number/title.
export async function getActivityFeedForBuilding(
  buildingId: number,
  opts: {
    buildingProjectId?: number;
    actionTypes?: PMActivityActionType[];
    actorIds?: number[];
    since?: string; // ISO timestamp lower bound (inclusive)
    until?: string; // ISO timestamp upper bound (exclusive)
    unreadOnly?: boolean;
    currentUserId?: number; // required when unreadOnly is set
    offset?: number;
    limit?: number;
  } = {}
): Promise<{ data: PMIssueActivity[]; count: number }> {
  const {
    buildingProjectId,
    actionTypes,
    actorIds,
    since,
    until,
    unreadOnly,
    currentUserId,
    offset = 0,
    limit = 50,
  } = opts;
  let query = (supabase as any)
    .from("pm_issue_activity")
    .select("*", { count: "exact" })
    .eq("building_id", buildingId);
  if (buildingProjectId != null) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  if (actionTypes && actionTypes.length > 0) {
    query = query.in("action_type", actionTypes);
  }
  if (actorIds && actorIds.length > 0) {
    query = query.in("actor_id", actorIds);
  }
  if (since) {
    query = query.gte("created_at", since);
  }
  if (until) {
    query = query.lt("created_at", until);
  }
  // "Unread only" = the same set that drives the red dot: newer than the user's
  // last-seen mark and not authored by the user (system rows still count).
  if (unreadOnly && currentUserId) {
    const lastSeen = await getNotificationsLastSeen(buildingId, currentUserId);
    if (lastSeen) query = query.gt("created_at", lastSeen);
    query = query.or(`actor_id.is.null,actor_id.neq.${currentUserId}`);
  }
  const { data, count, error } = await query
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as PMIssueActivity[];
  await hydrateActivityActors(rows);

  // Hydrate the issue ref (number + title) for each row's "#N Title" link.
  const issueIds = Array.from(new Set(rows.map((r) => r.issue_id)));
  if (issueIds.length > 0) {
    const { data: issues, error: iErr } = await (supabase as any)
      .from("pm_issues")
      .select("id, issue_number, title")
      .in("id", issueIds);
    if (iErr) throw new Error(iErr.message);
    const refs = (issues ?? []) as { id: number; issue_number: number; title: string }[];
    await applyDocumentRootTitles(refs);
    const issueMap = new Map<number, { issue_number: number; title: string }>(
      refs.map((i) => [i.id, { issue_number: i.issue_number, title: i.title }])
    );
    for (const r of rows) {
      const ref = issueMap.get(r.issue_id);
      if (ref) {
        r.issue_number = ref.issue_number;
        r.issue_title = ref.title;
      }
    }
  }
  return { data: rows, count: count ?? 0 };
}

const PROTOCOL_ACTIVITY_PAGE_SIZE = 200;
const PROTOCOL_ISSUE_BATCH_SIZE = 200;
/**
 * How many issue batches are in flight at once. At the activity cap there are
 * 25 of them and each is three round trips deep, so a serial loop is ~75
 * requests one after another behind the export's spinner. Four at a time cuts
 * that to ~20 without opening 25 sockets and starving the rest of the tab.
 */
const PROTOCOL_ISSUE_CONCURRENCY = 4;
/**
 * Ceiling on the activity one export pulls. An unbounded month on a busy
 * building would otherwise sit behind the spinner for minutes. Hitting the cap
 * keeps the newest rows and sets `truncated`, which the document prints as a
 * caveat — a short document that says so beats a long wait or a silent
 * half-truth.
 */
const PROTOCOL_ACTIVITY_LIMIT = 5000;

/**
 * What one export asks for. `signal` is the export dialog's Cancel: the fan-out
 * is up to 25 activity pages plus 25 issue batches three round trips deep, and
 * nothing else stops it once it has started.
 */
type ProtocolQuery = {
  since: string;
  until: string;
  buildingProjectId?: number;
  signal?: AbortSignal;
};

/** Equality filters an export's issue lookup shares with its activity query. */
function protocolIssueScope(
  buildingId: number,
  buildingProjectId?: number
): Record<string, number> {
  return buildingProjectId == null
    ? { building_id: buildingId }
    : { building_id: buildingId, building_project_id: buildingProjectId };
}

/**
 * The issue fields the minutes print. Deliberately NOT listIssues: that
 * hydrates linked documents (with folder-breadcrumb resolution), BIM elements,
 * comment counts and unread mentions, none of which reach the PDF.
 *
 * The project filter matches the one the activity query applies through its
 * join, so both halves of the export answer the same question: which issues
 * are in this project NOW and were touched in the period.
 */
async function fetchProtocolIssues(
  buildingId: number,
  issueIds: number[],
  buildingProjectId?: number,
  signal?: AbortSignal
): Promise<PMIssue[]> {
  if (issueIds.length === 0) return [];
  // Soft-deleted issues cannot come back: pm_issues_select is
  // `deleted_at IS NULL AND has_permission(…)`, so `authenticated` never reads
  // one. The activity query drops their rows through the same policy (see
  // protocolActivityPage), which is what keeps the two halves agreeing.
  const { data, error } = await withSignal(
    (supabase as any)
      .from("pm_issues")
      .select("*")
      .in("id", issueIds)
      .match(protocolIssueScope(buildingId, buildingProjectId)),
    signal
  );
  if (error) throw new Error(error.message);
  const issues = (data ?? []) as PMIssue[];
  if (issues.length === 0) return issues;
  return hydrateProtocolRelations(issues, signal);
}

/**
 * The same lookup over every issue the period touched. `.in(id, …)` travels in
 * a GET query string, so the ids go in fixed-size batches; the batches are
 * independent, so a few run at once rather than one after another. Results are
 * written back by index, keeping the caller's order regardless of who finishes
 * first, and a rejected batch rejects the export.
 */
async function fetchProtocolIssuesBatched(
  buildingId: number,
  issueIds: number[],
  buildingProjectId?: number,
  signal?: AbortSignal
): Promise<PMIssue[]> {
  const batches: number[][] = [];
  for (let start = 0; start < issueIds.length; start += PROTOCOL_ISSUE_BATCH_SIZE) {
    batches.push(issueIds.slice(start, start + PROTOCOL_ISSUE_BATCH_SIZE));
  }
  const results: PMIssue[][] = new Array(batches.length);
  let next = 0;
  const worker = async () => {
    for (let index = next++; index < batches.length; index = next++) {
      results[index] = await fetchProtocolIssues(
        buildingId,
        batches[index],
        buildingProjectId,
        signal
      );
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(PROTOCOL_ISSUE_CONCURRENCY, batches.length) }, worker)
  );
  return results.flat();
}

/** The two relations the printed issue table reads, and nothing else. */
async function hydrateProtocolRelations(
  issues: PMIssue[],
  signal?: AbortSignal
): Promise<PMIssue[]> {
  const ids = issues.map((issue) => issue.id);
  const [assigneesByIssue, disciplinesByIssue] = await Promise.all([
    hydrateAssignees(ids, signal),
    hydrateDisciplines(ids, signal),
  ]);
  for (const issue of issues) {
    issue.assignees = assigneesByIssue.get(issue.id) ?? [];
    issue.disciplines = disciplinesByIssue.get(issue.id) ?? [];
  }
  return issues;
}

/**
 * Page the raw activity rows the export prints, newest-first.
 *
 * Deliberately NOT getActivityFeedForBuilding: the feed asks for an exact
 * count and hydrates actor profiles plus issue number/title refs, and the
 * minutes use none of it — renderActivityLine reads only the trigger's
 * `metadata` snapshot, and the issue rows are fetched separately. At the cap
 * that would be three serial round trips per page (75 instead of 25) plus 25
 * exact counts over pm_issue_activity, all behind the export spinner.
 *
 * A project scope filters on the parent issue's CURRENT project via an inner
 * join rather than on the row's own `building_project_id`, which is a snapshot
 * of where the issue sat when the row was written. Filtering on the snapshot
 * makes the two halves of the export disagree: an issue moved between projects
 * would be listed under the project it left and be missing entirely from the
 * one it joined.
 *
 * The join is there whether or not a project is named, because it is also what
 * keeps a scoped and an unscoped export telling the same story. It is subject
 * to pm_issues RLS, so an activity row whose issue cannot be read — a
 * soft-deleted one, above all — has a null embed and `!inner` drops it. With
 * the join on one branch only, the same deleted issue in the same period was
 * silently absent from a scoped export and the reason for a "changes could not
 * be read" caveat on the unscoped one. Now neither prints it, and the
 * caveat is left to mean what it says: a genuine anomaly between the two
 * queries.
 */
async function fetchProtocolActivity(
  buildingId: number,
  opts: ProtocolQuery
): Promise<{ activity: PMIssueActivity[]; truncated: boolean }> {
  const activity: PMIssueActivity[] = [];

  for (;;) {
    const remaining = PROTOCOL_ACTIVITY_LIMIT - activity.length;
    if (remaining <= 0) break;
    const limit = Math.min(PROTOCOL_ACTIVITY_PAGE_SIZE, remaining);
    const { data, error } = await protocolActivityPage(buildingId, opts, activity.at(-1), limit);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as PMIssueActivity[];
    // The join is a filter, not payload — drop the embed so an activity row
    // stays the shape every consumer expects.
    for (const row of rows) delete (row as { pm_issues?: unknown }).pm_issues;
    activity.push(...rows);
    // Terminate on a short page rather than on a count: PostgREST can answer
    // with a null count, and a count-driven loop would then stop after the
    // first page and truncate the export to 200 rows without a word.
    if (rows.length < limit) break;
  }

  // A period whose activity lands exactly on the cap is reported as truncated;
  // proving otherwise would cost another round trip to see an empty page.
  return { activity, truncated: activity.length >= PROTOCOL_ACTIVITY_LIMIT };
}

/**
 * One page of that query, taken AFTER `cursor` rather than at an offset.
 *
 * Every preset's range ends at tomorrow 00:00, so the period is still open
 * while the loop pages: a change made during the meeting is inserted at offset
 * 0 and shifts every later row down one. `.range(activity.length, …)` would
 * then re-read rows the previous page already collected — the PDF prints that
 * change twice, and the duplicates spend the cap, truncating a period that
 * would have fit. Keyset paging is stable against that insert; the ordering
 * below already makes (created_at, id) a unique, deterministic key.
 */
/**
 * Every activity row the period contains, as a query the caller projects.
 *
 * Shared by the export and the step-1 count so the two can never disagree on
 * what "in this period" means. The `pm_issues!inner(...)` embed is a FILTER,
 * not payload — it is what drops rows whose issue is soft-deleted or invisible
 * under RLS — so every projection must keep it, whatever else it selects.
 */
function protocolActivityQuery(buildingId: number, opts: ProtocolQuery, columns: string) {
  let query = (supabase as any)
    .from("pm_issue_activity")
    .select(columns)
    .eq("building_id", buildingId)
    .gte("created_at", opts.since)
    .lt("created_at", opts.until);
  if (opts.buildingProjectId != null) {
    query = query.eq("pm_issues.building_project_id", opts.buildingProjectId);
  }
  return query;
}

function protocolActivityPage(
  buildingId: number,
  opts: ProtocolQuery,
  cursor: PMIssueActivity | undefined,
  limit: number
) {
  let query = protocolActivityQuery(buildingId, opts, "*, pm_issues!inner(building_project_id)");
  if (cursor) {
    // Quoted: a timestamptz carries `:` and `+`, both reserved inside a
    // logical filter.
    query = query.or(
      `created_at.lt."${cursor.created_at}",and(created_at.eq."${cursor.created_at}",id.lt.${cursor.id})`
    );
  }
  return withSignal(
    query.order("created_at", { ascending: false }).order("id", { ascending: false }).limit(limit),
    opts.signal
  );
}

/**
 * How many distinct issues the period touched, without fetching any of them.
 *
 * Step 1 shows this before the user commits to a period, so it deliberately
 * pays for far less than the export: the same membership filter, but projecting
 * only `issue_id` rather than whole activity rows (the `metadata` jsonb is the
 * bulk of one), and none of the issue/assignee/discipline hydration.
 *
 * `count: "exact", head: true` is NOT usable here — it would count ACTIVITY
 * ROWS, several per issue, so it overcounts by multiples. Distinct issues can
 * only be had by reading the ids and deduping.
 *
 * The number is an upper bound on what the export finally lists: an issue whose
 * row the caller cannot read is counted here and dropped by `getProtocolData`
 * (its `omitted` flag). `truncated` means the cap was hit and the real total is
 * higher, so the caller must say "at least N" rather than "N".
 */
export async function countProtocolIssues(
  buildingId: number,
  opts: ProtocolQuery
): Promise<{ count: number; truncated: boolean }> {
  const seen = new Set<number>();
  let cursor: { created_at: string; id: number } | undefined;
  let scanned = 0;

  for (;;) {
    const remaining = PROTOCOL_ACTIVITY_LIMIT - scanned;
    if (remaining <= 0) break;
    const limit = Math.min(PROTOCOL_ACTIVITY_PAGE_SIZE, remaining);

    let query = protocolActivityQuery(
      buildingId,
      opts,
      "id, created_at, issue_id, pm_issues!inner(building_project_id)"
    );
    if (cursor) {
      // Quoted: a timestamptz carries `:` and `+`, both reserved inside a
      // logical filter. Same keyset paging as the export, for the same reason —
      // an insert during the loop must not shift rows into a page twice.
      query = query.or(
        `created_at.lt."${cursor.created_at}",and(created_at.eq."${cursor.created_at}",id.lt.${cursor.id})`
      );
    }
    const { data, error } = await withSignal(
      query
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(limit),
      opts.signal
    );
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as Array<{ id: number; created_at: string; issue_id: number }>;
    for (const row of rows) seen.add(row.issue_id);
    scanned += rows.length;
    cursor = rows.at(-1);
    // Terminate on a short page rather than on a count, exactly as the export
    // does: PostgREST can answer with a null count.
    if (rows.length < limit) break;
  }

  return { count: seen.size, truncated: scanned >= PROTOCOL_ACTIVITY_LIMIT };
}

/**
 * Every comment WRITTEN IN THE PERIOD on the given issues, for the protocol
 * export.
 *
 * Bounded by the same `[since, until)` the activity query is, because the
 * document says it is: the section reads "No comments were recorded in this
 * period", and an unbounded fetch files a comment from March under a protocol
 * for today. An issue is in the period because it was touched in it, not
 * because it was born there — most carry older comments.
 *
 * Deliberately not `listComments`: that is per-issue and costs up to five round
 * trips each. This is the same two-step the other relations use — the rows, then
 * their authors — because `pm_issue_comments` declares no FK to `user_profiles`
 * and a PostgREST embed is therefore impossible.
 *
 * Keyset-paged on `(issue_id, id)` for the same reason the activity fetch is: an
 * unpaged `.in()` stops at PostgREST's row cap and would drop the tail of a busy
 * period without saying so.
 *
 * Soft-deleted comments are excluded, matching `listComments`. Note this makes
 * the export's count legitimately lower than the list's badge, which counts them
 * — the list is the one that is wrong.
 */
/**
 * Ceiling on what one export pulls, for the same reason the activity has one:
 * comment bodies are the widest rows the export reads, and a busy quarter is
 * otherwise unbounded. Hitting it sets `truncated`, which the document prints
 * as its own caveat rather than passing a slice off as the whole.
 */
const PROTOCOL_COMMENT_LIMIT = 5000;

export interface ProtocolComments {
  comments: Map<number, PMIssueComment[]>;
  /** The scan hit the cap, so some of the period's comments are missing. */
  truncated: boolean;
  /**
   * The issues the cap cut short. Their comment lists are a slice, so their
   * counts are unknown — never zero, which is what the raw list would say.
   */
  incomplete: Set<number>;
}

/**
 * One keyset page of a period's comments, resuming after `cursor`.
 *
 * The cursor carries both halves of `(issue_id, id)` because a page
 * legitimately ends mid-issue; paging on the issue alone would skip the rest of
 * that issue's comments.
 */
async function protocolCommentPage(
  issueIds: number[],
  cursor: PMIssueComment | undefined,
  opts: { since: string; until: string; signal?: AbortSignal }
): Promise<PMIssueComment[]> {
  let query = (supabase as any)
    .from("pm_issue_comments")
    .select("id, issue_id, author_id, body, created_at, modified_at, modified_by")
    .in("issue_id", issueIds)
    .gte("created_at", opts.since)
    .lt("created_at", opts.until)
    .is("deleted_at", null)
    .order("issue_id", { ascending: true })
    .order("id", { ascending: true })
    .limit(ISSUE_LINK_PAGE_SIZE);
  if (cursor) {
    query = query.or(
      `issue_id.gt.${cursor.issue_id},and(issue_id.eq.${cursor.issue_id},id.gt.${cursor.id})`
    );
  }
  const { data, error } = await withSignal(query, opts.signal);
  if (error) throw new Error(error.message);
  return (data ?? []) as PMIssueComment[];
}

/** Read every protocol comment the period asks for, one page past the cap so
 *  that an overflow can be told apart from an exact fit. */
async function readProtocolCommentRows(
  issueIds: number[],
  opts: { since: string; until: string; signal?: AbortSignal }
): Promise<PMIssueComment[]> {
  const rows: PMIssueComment[] = [];
  let cursor: PMIssueComment | undefined;
  for (;;) {
    const page = await protocolCommentPage(issueIds, cursor, opts);
    rows.push(...page);
    if (page.length < ISSUE_LINK_PAGE_SIZE) break;
    // `>=` would call a period that lands exactly on the cap truncated — 5000
    // is a multiple of the page size, so an exact fit is reachable — and print
    // a caveat saying only some comments are listed when in fact all are. Read
    // one page past the cap instead: only rows beyond it prove overflow.
    if (rows.length > PROTOCOL_COMMENT_LIMIT) break;
    cursor = page[page.length - 1];
  }
  return rows;
}

/**
 * Trim `rows` to the cap, recording which issues the cut left short. Returns
 * whether it bit at all.
 *
 * Rows arrive in ascending `(issue_id, id)`, so the cap always bites at the
 * HIGH ids: the issue holding the last kept row may be short the rest of its
 * comments, and every requested issue above it was never read at all. Naming
 * them is what stops the document printing "0" — a wrong number stated as
 * fact — for an issue whose comments simply did not fit.
 */
function applyProtocolCommentCap(
  rows: PMIssueComment[],
  issueIds: number[],
  incomplete: Set<number>
): boolean {
  if (rows.length <= PROTOCOL_COMMENT_LIMIT) return false;
  const boundary = rows[PROTOCOL_COMMENT_LIMIT - 1].issue_id;
  const boundaryCut = rows[PROTOCOL_COMMENT_LIMIT].issue_id === boundary;
  for (const id of issueIds) {
    if (id > boundary || (boundaryCut && id === boundary)) incomplete.add(id);
  }
  // The page that proved the overflow is dropped again, so the cap stays the
  // number of comments an export can actually hold.
  rows.length = PROTOCOL_COMMENT_LIMIT;
  return true;
}

export async function fetchProtocolComments(
  issueIds: number[],
  opts: { since: string; until: string; signal?: AbortSignal }
): Promise<ProtocolComments> {
  const byIssue = new Map<number, PMIssueComment[]>();
  const incomplete = new Set<number>();
  if (issueIds.length === 0) return { comments: byIssue, truncated: false, incomplete };

  const rows = await readProtocolCommentRows(issueIds, opts);
  const truncated = applyProtocolCommentCap(rows, issueIds, incomplete);
  if (rows.length === 0) return { comments: byIssue, truncated, incomplete };

  const authors = await fetchRowsByIds<UserProfile>(
    "user_profiles",
    "id, first_name, last_name, email, avatar_url, color",
    Array.from(new Set(rows.map((row) => row.author_id))),
    opts.signal
  );
  for (const row of rows) {
    row.author = authors.get(row.author_id);
    const existing = byIssue.get(row.issue_id);
    if (existing) existing.push(row);
    else byIssue.set(row.issue_id, [row]);
  }
  return { comments: byIssue, truncated, incomplete };
}

/**
 * Fetch every issue touched in an inclusive local date range, together with
 * the immutable activity rows that made it relevant. The activity query is
 * the source of truth for period membership, so relation-only changes such as
 * comments, assignees and linked files are included even when pm_issues itself
 * did not receive an UPDATE.
 */
export async function getProtocolData(
  buildingId: number,
  opts: ProtocolQuery
): Promise<ProtocolData> {
  const { activity, truncated } = await fetchProtocolActivity(buildingId, opts);

  const issueIds = Array.from(new Set(activity.map((row) => row.issue_id)));
  const issues = await fetchProtocolIssuesBatched(
    buildingId,
    issueIds,
    opts.buildingProjectId,
    opts.signal
  );

  // Activity whose issue row did not come back has nowhere to be printed — the
  // document groups every line under its issue. It should not happen (the
  // activity query joins the same issues), but an issue moved or hard-deleted
  // between the two queries, or an RLS asymmetry, would drop changes silently.
  // A console line is for us; the flag is for the reader, whose PDF would
  // otherwise read as a complete record of the period.
  const unresolved = issueIds.length - issues.length;
  if (unresolved > 0) {
    console.warn(
      `Protocol: ${unresolved} issue(s) with activity in range could not be read; their changes are omitted from the export.`
    );
  }

  return { issues, activity, truncated, omitted: unresolved > 0 };
}

// WHA-3733: the user's last-seen high-water-mark for a building's notifications.
export async function getNotificationsLastSeen(
  buildingId: number,
  userId: number
): Promise<string | null> {
  if (!userId) return null;
  const { data, error } = await (supabase as any)
    .from("pm_notifications_last_seen")
    .select("last_seen_at")
    .eq("user_id", userId)
    .eq("building_id", buildingId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data?.last_seen_at as string | undefined) ?? null;
}

// WHA-3733: stamp "notifications opened now" for the current user + building.
export async function markNotificationsSeen(buildingId: number, userId: number): Promise<void> {
  if (!userId) return;
  const { error } = await (supabase as any)
    .from("pm_notifications_last_seen")
    .upsert(
      { user_id: userId, building_id: buildingId, last_seen_at: new Date().toISOString() },
      { onConflict: "user_id,building_id" }
    );
  if (error) throw new Error(error.message);
}

// WHA-3733: count activity the user hasn't seen — newer than their last-seen
// mark and authored by someone else. Drives the nav red dot.
export async function getUnreadActivityCountForBuilding(
  buildingId: number,
  currentUserId: number,
  buildingProjectId?: number | null
): Promise<number> {
  if (!currentUserId) return 0;
  const lastSeen = await getNotificationsLastSeen(buildingId, currentUserId);
  let query = (supabase as any)
    .from("pm_issue_activity")
    .select("*", { count: "exact", head: true })
    .eq("building_id", buildingId)
    // Exclude only the user's OWN actions. NULL-actor (system / service-role)
    // rows are not "mine", so they must still count — `actor_id <> me` alone
    // would drop them since NULL <> x is NULL in SQL.
    .or(`actor_id.is.null,actor_id.neq.${currentUserId}`);
  if (buildingProjectId != null) {
    query = query.eq("building_project_id", buildingProjectId);
  }
  if (lastSeen) {
    query = query.gt("created_at", lastSeen);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * No live issue answers this id: deleted, or hidden from the caller by RLS.
 * Told apart by `name`, not `instanceof`, so a module mock that omits the
 * class cannot turn the check into a TypeError.
 */
export class IssueNotFoundError extends Error {
  constructor(issueId: number) {
    super(`Issue ${issueId} not found`);
    this.name = "IssueNotFoundError";
  }
}

export async function getIssue(issueId: number): Promise<PMIssue> {
  const issue = await findIssue(issueId);
  if (!issue) throw new IssueNotFoundError(issueId);
  return issue;
}

/**
 * One issue with its relations, or null when there is no such live issue —
 * deleted, or never visible to this user. Separate from `getIssue` so a caller
 * refreshing a row it already shows can tell "gone" from "the read failed".
 */
export async function findIssue(issueId: number): Promise<PMIssue | null> {
  const { data, error } = await (supabase as unknown as SupabaseClient)
    .from("pm_issues")
    .select("*")
    .eq("id", issueId)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;
  const issue = data as PMIssue;
  // WHA-3667: hydrate the four relation sets in parallel — they're independent,
  // so running them concurrently cuts the detail-modal open time from ~8-10
  // serial round-trips to the slowest single chain.
  // WHA-3521: linked_elements must hydrate here too (the detail panel opens via
  // selectIssue → getIssue); listIssues hydrates them separately for the badge.
  const [disciplines, assignees, linkedDocuments, linkedElements, viewpoint] = await Promise.all([
    getIssueDisciplines(issueId),
    getIssueAssignees(issueId),
    getIssueDocuments(issueId),
    getIssueElements(issueId),
    // A saved view is an enhancement; nothing in the detail panel depends on
    // it. Only the missing-relation codes degrade inside getIssueViewpoint, so
    // anything else (a 42501 while the table's SELECT grant is still catching
    // up — the migration ships from another repo) would otherwise take every
    // issue-detail open down with it.
    getIssueViewpoint(issueId).catch((e) => {
      console.warn("Failed to load issue viewpoint", e);
      return null;
    }),
  ]);
  issue.disciplines = disciplines;
  issue.assignees = assignees;
  issue.linked_documents = linkedDocuments;
  issue.linked_elements = linkedElements;
  issue.viewpoint = viewpoint;
  await applyDocumentRootTitles([issue]);
  return issue;
}

/**
 * Flush the per-status and overdue totals the issue filter shows. Both are
 * react-query'd with a staleTime, and neither is derived from the loaded page
 * — so without this the picker can contradict the board it sits beside for up
 * to a minute after the user's own create, status change or delete. Called
 * from every issue write for that reason; key-prefix invalidation covers every
 * building/project/search variant, and the cached payloads are three numbers.
 */
function invalidateIssueCountCaches(): void {
  queryClient.invalidateQueries({ queryKey: ["pm-status-counts"] });
  queryClient.invalidateQueries({ queryKey: ["pm-overdue-count"] });
}

export async function createIssue(data: Partial<PMIssue>): Promise<PMIssue> {
  const { data: row, error } = await (supabase as any)
    .from("pm_issues")
    .insert(data)
    .select()
    .single();

  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
  return row as PMIssue;
}

/** Create the PM issue and its exact DWG point in one database transaction.
 * The RPC rolls the issue insert back if the anchor fails validation or RLS. */
export async function createIssueWithDrawingAnchor(
  data: Partial<PMIssue>,
  anchor: Pick<DrawingIssueAnchor, "document_id" | "x" | "y">
): Promise<PMIssue> {
  const buildingId = requireDrawingIssueValue(data.building_id);
  const orgId = requireDrawingIssueValue(data.org_id);
  const title = requireDrawingIssueValue(data.title);
  const status = requireDrawingIssueValue(data.status);
  const buildingProjectId = requireDrawingIssueValue(data.building_project_id);

  const { data: row, error } = await (supabase as unknown as SupabaseClient)
    .rpc("pm_create_issue_with_drawing_anchor", {
      p_building_id: buildingId,
      p_org_id: orgId,
      p_title: title,
      p_status: status,
      p_start_date: data.start_date ?? null,
      p_due_date: data.due_date ?? null,
      p_building_project_id: buildingProjectId,
      p_parent_issue_id: data.parent_issue_id ?? null,
      p_document_id: anchor.document_id,
      p_x: anchor.x,
      p_y: anchor.y,
    })
    .single();

  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
  return row as PMIssue;
}

function requireDrawingIssueValue<T>(value: T | null | undefined): T {
  if (value == null) throw new Error("Drawing issue scope is incomplete");
  return value;
}

export async function updateIssue(issueId: number, data: Partial<PMIssue>): Promise<void> {
  const { error } = await (supabase as any).from("pm_issues").update(data).eq("id", issueId);

  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
}

export async function bulkUpdateIssues(issueIds: number[], data: Partial<PMIssue>): Promise<void> {
  if (issueIds.length === 0) return;
  const { error } = await (supabase as any).from("pm_issues").update(data).in("id", issueIds);
  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
}

export async function softDeleteIssue(issueId: number): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issues")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", issueId);

  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
}

// ---------------------------------------------------------------------------
// Issue disciplines
// ---------------------------------------------------------------------------

export async function setIssueDisciplines(issueId: number, disciplineIds: number[]): Promise<void> {
  // Differential write (WHA-3732): only delete genuinely-removed rows and
  // insert genuinely-added ones. A full delete-all + insert-all would make the
  // pm_issue_activity triggers log a spurious remove+add for every unchanged
  // discipline on every save.
  const { data: existing, error: readErr } = await (supabase as any)
    .from("pm_issue_disciplines")
    .select("discipline_id")
    .eq("issue_id", issueId);
  if (readErr) throw new Error(readErr.message);

  const current = new Set<number>(
    (existing ?? []).map((r: { discipline_id: number }) => r.discipline_id)
  );
  const next = new Set<number>(disciplineIds);
  const toRemove = [...current].filter((id) => !next.has(id));
  const toAdd = [...next].filter((id) => !current.has(id));

  if (toRemove.length > 0) {
    const { error } = await (supabase as any)
      .from("pm_issue_disciplines")
      .delete()
      .eq("issue_id", issueId)
      .in("discipline_id", toRemove);
    if (error) throw new Error(error.message);
  }
  if (toAdd.length > 0) {
    const rows = toAdd.map((discipline_id) => ({ issue_id: issueId, discipline_id }));
    const { error } = await (supabase as any).from("pm_issue_disciplines").insert(rows);
    if (error) throw new Error(error.message);
  }
}

export async function getIssueDisciplines(issueId: number): Promise<Discipline[]> {
  // Fetch discipline IDs from junction
  const { data: junctionRows, error: junctionError } = await supabase
    .from("pm_issue_disciplines")
    .select("discipline_id")
    .eq("issue_id", issueId);
  if (junctionError) throw new Error(junctionError.message);

  const disciplineIds: number[] = (junctionRows ?? []).map(
    (r: { discipline_id: number }) => r.discipline_id
  );
  if (disciplineIds.length === 0) return [];

  // Fetch actual discipline records
  return readDisciplines(disciplineIds);
}

// ---------------------------------------------------------------------------
// Issue assignees
// ---------------------------------------------------------------------------

export async function setIssueAssignees(issueId: number, userIds: number[]): Promise<void> {
  // Differential write (WHA-3732): see setIssueDisciplines — only touch rows
  // that actually changed so the activity-log triggers don't record spurious
  // assignee_removed/assignee_added pairs on no-op saves.
  const { data: existing, error: readErr } = await (supabase as any)
    .from("pm_issue_assignees")
    .select("user_id")
    .eq("issue_id", issueId);
  if (readErr) throw new Error(readErr.message);

  const current = new Set<number>((existing ?? []).map((r: { user_id: number }) => r.user_id));
  const next = new Set<number>(userIds);
  const toRemove = [...current].filter((id) => !next.has(id));
  const toAdd = [...next].filter((id) => !current.has(id));

  if (toRemove.length > 0) {
    const { error } = await (supabase as any)
      .from("pm_issue_assignees")
      .delete()
      .eq("issue_id", issueId)
      .in("user_id", toRemove);
    if (error) throw new Error(error.message);
  }
  if (toAdd.length > 0) {
    const rows = toAdd.map((user_id) => ({ issue_id: issueId, user_id }));
    const { error } = await (supabase as any).from("pm_issue_assignees").insert(rows);
    if (error) throw new Error(error.message);
  }
}

/**
 * WHA-3507 — return the user profiles allowed to access `projectId` so the
 * frontend can scope issue assignee pickers and @-mention suggestions to
 * actual project members. Calls the `get_project_assignable_users` RPC
 * which mirrors the RLS predicate on `building_projects` and returns an
 * empty set when the caller cannot see the project themselves.
 */
export async function fetchProjectAssignableUsers(projectId: number): Promise<UserProfile[]> {
  const { data, error } = await (supabase as any).rpc("get_project_assignable_users", {
    p_project_id: projectId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as UserProfile[];
}

export async function getIssueAssignees(issueId: number): Promise<UserProfile[]> {
  const { data: junctionRows, error: junctionError } = await (supabase as any)
    .from("pm_issue_assignees")
    .select("user_id")
    .eq("issue_id", issueId);
  if (junctionError) throw new Error(junctionError.message);

  const userIds: number[] = (junctionRows ?? []).map((r: { user_id: number }) => r.user_id);
  if (userIds.length === 0) return [];

  const { data, error } = await (supabase as any)
    .from("user_profiles")
    .select("id, first_name, last_name, email, avatar_url, color")
    .in("id", userIds);
  if (error) throw new Error(error.message);

  return (data ?? []) as UserProfile[];
}

// ---------------------------------------------------------------------------
// Issue documents
// ---------------------------------------------------------------------------

/**
 * Link a document to an issue. Idempotent: `pm_issue_documents` is keyed by
 * (issue_id, document_id), so a plain insert of a link that already exists
 * raises 23505. Callers batch these (the picker attaches a whole selection
 * with `Promise.all`), and any caller can be holding a stale "already
 * attached" list — one duplicate would otherwise reject the batch and report
 * a failure for attachments that did go through. Re-linking is a no-op.
 */
export async function addIssueDocument(issueId: number, documentId: number): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issue_documents")
    .upsert(
      { issue_id: issueId, document_id: documentId },
      { onConflict: "issue_id,document_id", ignoreDuplicates: true }
    );
  if (error) throw new Error(error.message);
}

export async function removeIssueDocument(issueId: number, documentId: number): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issue_documents")
    .delete()
    .eq("issue_id", issueId)
    .eq("document_id", documentId);
  if (error) throw new Error(error.message);
}

/**
 * WHA-3270 — reverse lookup of {@link getIssueDocuments}.
 *
 * Given a batch of document ids, return a Map from `document_id` to the
 * list of PM issues that reference it via `pm_issue_documents`. Used by
 * the documents/timeline/project pages to render the `LinkedIssuesChip`
 * next to each preview button.
 *
 * Soft-deleted issues are skipped so a closed-and-removed issue doesn't
 * show up on a doc's chip. The result keys exactly cover the input ids
 * that have at least one linked issue (missing keys = empty array).
 */
export async function getIssuesForDocuments(
  documentIds: number[]
): Promise<Map<number, LinkedIssueSummary[]>> {
  const result = new Map<number, LinkedIssueSummary[]>();
  if (documentIds.length === 0) return result;

  const { data, error } = await (supabase as any)
    .from("pm_issue_documents")
    .select(
      "document_id, pm_issues!inner(id, issue_number, title, building_id, status, deleted_at)"
    )
    .in("document_id", documentIds);
  if (error) throw new Error(error.message);

  for (const row of data ?? []) addLinkedIssueRow(result, row);
  await applyDocumentRootTitles(Array.from(result.values()).flat());

  // Stable per-document ordering: by issue_number ascending.
  for (const list of result.values()) {
    list.sort((a, b) => a.issue_number - b.issue_number);
  }

  return result;
}

/** Shape of a linked issue summarised on a document's chip (WHA-3270). */
export type LinkedIssueSummary = {
  id: number;
  issue_number: number;
  title: string;
  building_id: number;
  status: "in_progress" | "resolved" | "closed";
  /** WHA-3671: BIM elements this issue links on the currently-loaded models,
   * for the viewer's "Go to element" action. Populated only by
   * {@link getIssuesForModel}; absent for document-only links. */
  focusableElements?: { guid: string; docId: number }[];
  /** Assignees, shown on the viewer's compact issue cards. Populated by
   * {@link getIssuesForModel} and {@link getIssuesForAnchor}. */
  assignees?: UserProfile[];
  /** True when the issue carries a saved 3D view, so the viewer's issue rows
   * can offer "Restore view". Populated by {@link getIssuesForModel} and
   * {@link getIssuesForAnchor}; always false before the viewpoint migration
   * has applied. */
  hasViewpoint?: boolean;
};

/** Fold one `pm_issue_documents` row into `result`, skipping soft-deleted issues. */
function addLinkedIssueRow(result: Map<number, LinkedIssueSummary[]>, row: unknown): void {
  const r = row as {
    document_id: number;
    pm_issues: (LinkedIssueSummary & { deleted_at: string | null }) | null;
  };
  if (!r.pm_issues || r.pm_issues.deleted_at) return;
  const { id, issue_number, title, building_id, status } = r.pm_issues;
  const list = result.get(r.document_id) ?? [];
  list.push({ id, issue_number, title, building_id, status });
  result.set(r.document_id, list);
}

export async function getIssueDocuments(issueId: number): Promise<LinkedDocument[]> {
  const { data: junctionRows, error: junctionError } = await (supabase as any)
    .from("pm_issue_documents")
    .select("document_id")
    .eq("issue_id", issueId);
  if (junctionError) throw new Error(junctionError.message);

  const documentIds: number[] = (junctionRows ?? []).map(
    (r: { document_id: number }) => r.document_id
  );
  if (documentIds.length === 0) return [];

  // The same columns as the bulk select inside listIssues (above). When
  // file_path was missing here, the expanded "Linked Documents" list opened
  // with an undefined storage path and the viewer toasted "This file is no
  // longer available" (WHA-3333).
  const { data, error } = await (supabase as any)
    .from("documents")
    .select(LINKED_DOCUMENT_COLUMNS)
    .in("id", documentIds);
  if (error) throw new Error(error.message);

  const documents = (data ?? []) as LinkedDocument[];
  // WHA-3457: resolve folder-path breadcrumbs for the tooltip.
  await attachDocumentBreadcrumbs(documents);
  return documents;
}

/**
 * The documents attached to each of `issueIds`, for the protocol's "include the
 * issues' attachments". Same columns and breadcrumbs as the issue panel's own
 * band, so a file shows the same tile in both. The ids go in the same batches
 * as the protocol's issue fetch — `.in()` travels in the GET query string.
 */
export async function getProtocolIssueDocuments(
  issueIds: number[],
  signal?: AbortSignal
): Promise<Map<number, LinkedDocument[]>> {
  const links: Array<{ issue_id: number; document_id: number }> = [];
  for (let start = 0; start < issueIds.length; start += PROTOCOL_ISSUE_BATCH_SIZE) {
    links.push(
      ...(await fetchIssueLinks(
        "pm_issue_documents",
        "document_id",
        issueIds.slice(start, start + PROTOCOL_ISSUE_BATCH_SIZE),
        signal
      ))
    );
  }
  const byIssue = new Map<number, LinkedDocument[]>();
  if (links.length === 0) return byIssue;
  const documents = await fetchRowsByIds<LinkedDocument>(
    "documents",
    LINKED_DOCUMENT_COLUMNS,
    Array.from(new Set(links.map((link) => link.document_id))),
    signal
  );
  await attachDocumentBreadcrumbs(Array.from(documents.values()));
  for (const link of links) {
    const doc = documents.get(link.document_id);
    if (!doc) continue;
    const list = byIssue.get(link.issue_id) ?? [];
    list.push(doc);
    byIssue.set(link.issue_id, list);
  }
  return byIssue;
}

/**
 * Documents picked from Franklin, as full linked rows: the picker hands back
 * ids and names only, and a tile needs the storage path and size. Returned in
 * the order asked for; an id that does not come back (deleted meanwhile, or
 * unreadable) is left out.
 */
export async function getLinkedDocumentsByIds(ids: number[]): Promise<LinkedDocument[]> {
  if (ids.length === 0) return [];
  const documents = await fetchRowsByIds<LinkedDocument>("documents", LINKED_DOCUMENT_COLUMNS, ids);
  const ordered = ids
    .map((id) => documents.get(id))
    .filter((doc): doc is LinkedDocument => doc !== undefined);
  await attachDocumentBreadcrumbs(ordered);
  return ordered;
}

// ---------------------------------------------------------------------------
// Issue elements (WHA-3521). pm_issue_elements FKs to the stable anchor
// (data.ifc_element_anchors.id) but carries denormalised guid/name/type so the
// PM UI never needs to join back into the IFC tables. created_by is left to the
// column default (nullable audit field).
// ---------------------------------------------------------------------------

export async function getIssueElements(issueId: number): Promise<LinkedElement[]> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_elements")
    .select("anchor_id, element_guid, element_name, element_type, ifc_document_id")
    .eq("issue_id", issueId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as LinkedElement[];
}

// ---------------------------------------------------------------------------
// Saved 3D viewpoints (pm_issue_viewpoints)
// ---------------------------------------------------------------------------

/** Columns of `pm_issue_viewpoints` the app reads. Enumerated rather than `*`
 * so an added column can never change the shape under us. */
const VIEWPOINT_COLUMNS =
  "schema_version, camera, section, element_guid, anchor_id, model_document_ids, scene_anchor_document_id";

/**
 * True when PostgREST says the relation itself is absent — i.e. this app build
 * is deployed ahead of the migration that creates it.
 *
 * The viewpoint table lands in a separate repo (franklin-data-pipeline), so
 * there is a window where the app is live and the table is not. Every viewpoint
 * read degrades to "no viewpoint" in that window instead of throwing, because
 * these reads sit inside hydration paths that would otherwise take the whole
 * issues list down with them.
 */
export function isMissingRelation(error: { code?: string } | null | undefined): boolean {
  return error?.code === "42P01" || error?.code === "PGRST205";
}

/**
 * One cast for this table instead of one per query.
 *
 * `src/integrations/supabase/types.ts` was last generated 2026-08-04 and does
 * not know `pm_issue_viewpoints` (nor `pm_issue_elements`), which is why every
 * call in this file goes through `supabase as any`. Regenerating would pull in
 * months of unrelated schema drift, so the cast stays — but it is confined
 * here, and the row shapes are typed at each call site instead.
 */
const viewpointTable = () => (supabase as unknown as SupabaseClient).from("pm_issue_viewpoints");

/** The saved view for one issue, or null when it has none (or the table is not
 * there yet). Hydrated into {@link getIssue}, so both `/issues` entry paths —
 * row click and `?selected=` — get it for free. */
export async function getIssueViewpoint(issueId: number): Promise<IssueViewpoint | null> {
  const { data, error } = await viewpointTable()
    .select(VIEWPOINT_COLUMNS)
    .eq("issue_id", issueId)
    .maybeSingle();
  if (error) {
    if (isMissingRelation(error)) return null;
    throw new Error(error.message);
  }
  return (data as IssueViewpoint | null) ?? null;
}

/**
 * Which of these issues have a saved view. A chunked `IN` sweep rather than
 * N+1, so the viewer's issue lists can render the restore affordance per row.
 *
 * NEVER throws. Both callers hydrate it inside a `Promise.all` alongside the
 * issues themselves, and `hasViewpoint` is a per-row affordance — so any error
 * here (a 42501 while the table's SELECT grant is still catching up, the
 * migration shipping from another repo) would otherwise turn "one row lacks a
 * restore button" into "the whole issue list failed to load". Same trade as
 * {@link getIssue}'s catch around {@link getIssueViewpoint}.
 */
export async function getIssueIdsWithViewpoint(issueIds: number[]): Promise<Set<number>> {
  const found = new Set<number>();
  // Chunked for the same reason fetchRowsByIds is: PostgREST truncates at
  // max-rows (1000) without an error, so a bare `.in()` over a long list drops
  // its tail — and a row whose viewpoint went missing renders exactly like a
  // row that never had one. `issue_id` is UNIQUE here, so a chunk under the cap
  // can never come back short and no paging is needed on top.
  for (let start = 0; start < issueIds.length; start += ID_LOOKUP_CHUNK_SIZE) {
    const { data, error } = await viewpointTable()
      .select("issue_id")
      .in("issue_id", issueIds.slice(start, start + ID_LOOKUP_CHUNK_SIZE));
    if (error) {
      if (!isMissingRelation(error)) console.warn("Failed to load issue viewpoints", error);
      return new Set();
    }
    for (const row of (data ?? []) as { issue_id: number }[]) found.add(row.issue_id);
  }
  return found;
}

/**
 * Save (or replace) an issue's view. Upsert on `issue_id`, which the table
 * makes UNIQUE — re-saving a view updates the single row rather than piling up
 * revisions, and the DB trigger logs a `viewpoint_saved` activity either way.
 *
 * Coordinates must already be ABSOLUTE — see
 * `domains/buildings/components/ifc-viewer/issueViewpoint.ts`.
 */
export async function saveIssueViewpoint(
  issueId: number,
  viewpoint: IssueViewpoint,
  createdBy: number | null
): Promise<void> {
  const { error } = await viewpointTable().upsert(
    {
      issue_id: issueId,
      schema_version: viewpoint.schema_version,
      camera: viewpoint.camera,
      section: viewpoint.section,
      element_guid: viewpoint.element_guid,
      anchor_id: viewpoint.anchor_id,
      model_document_ids: viewpoint.model_document_ids,
      scene_anchor_document_id: viewpoint.scene_anchor_document_id,
      created_by: createdBy,
    },
    { onConflict: "issue_id" }
  );
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Native-DWG issue anchors (pm_issue_drawing_anchors)
// ---------------------------------------------------------------------------

const drawingAnchorTable = () =>
  (supabase as unknown as SupabaseClient).from("pm_issue_drawing_anchors");

interface DrawingAnchorJoinRow {
  issue_id: number;
  document_id: number;
  schema_version: number;
  x: number;
  y: number;
  pm_issues: {
    id: number;
    issue_number: number;
    title: string;
    building_id: number;
    status: DrawingIssueAnchor["status"];
    deleted_at: string | null;
  };
}

const DRAWING_ANCHOR_PAGE_SIZE = 500;

/** All live issue bubbles attached to the DWG documents currently loaded in
 * the viewer. Missing-table deployment windows degrade to an empty overlay. */
export async function getDrawingIssueAnchors(documentIds: number[]): Promise<DrawingIssueAnchor[]> {
  if (documentIds.length === 0) return [];
  const rows: DrawingAnchorJoinRow[] = [];
  let cursor: number | null = null;

  for (;;) {
    let query = drawingAnchorTable()
      .select(
        "issue_id, document_id, schema_version, x, y, pm_issues!inner(id, issue_number, title, building_id, status, deleted_at)"
      )
      .in("document_id", documentIds)
      .order("issue_id", { ascending: true });
    if (cursor != null) query = query.gt("issue_id", cursor);
    const { data, error } = await query.limit(DRAWING_ANCHOR_PAGE_SIZE);
    if (error) {
      if (isMissingRelation(error)) return [];
      throw new Error(error.message);
    }

    const page = (data ?? []) as unknown as DrawingAnchorJoinRow[];
    rows.push(...page);
    if (page.length < DRAWING_ANCHOR_PAGE_SIZE) break;
    cursor = page[page.length - 1].issue_id;
  }

  return rows
    .filter((row) => row.schema_version === 1 && row.pm_issues.deleted_at == null)
    .map((row) => ({
      // The filter above guarantees it; the row type says `number`, and the
      // anchor type pins the literal so a v2 payload cannot be read as v1.
      schema_version: 1 as const,
      issue_id: row.pm_issues.id,
      issue_number: row.pm_issues.issue_number,
      title: row.pm_issues.title,
      building_id: row.pm_issues.building_id,
      status: row.pm_issues.status,
      document_id: row.document_id,
      x: row.x,
      y: row.y,
    }))
    .sort((a, b) => a.issue_number - b.issue_number);
}

export async function addIssueElement(issueId: number, element: LinkedElement): Promise<void> {
  const { error } = await (supabase as unknown as SupabaseClient).from("pm_issue_elements").upsert(
    {
      issue_id: issueId,
      anchor_id: element.anchor_id,
      element_guid: element.element_guid,
      element_name: element.element_name,
      element_type: element.element_type,
      ifc_document_id: element.ifc_document_id,
    },
    { onConflict: "issue_id,anchor_id", ignoreDuplicates: true }
  );
  if (error) throw new Error(error.message);
}

export async function removeIssueElement(issueId: number, anchorId: number): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issue_elements")
    .delete()
    .eq("issue_id", issueId)
    .eq("anchor_id", anchorId);
  if (error) throw new Error(error.message);
}

/**
 * Reverse lookup for the viewer's right rail: PM issues that reference an
 * element, identified by its stable anchor id. RLS on pm_issues scopes
 * visibility; soft-deleted issues are skipped.
 */
export async function getIssuesForAnchor(anchorId: number): Promise<LinkedIssueSummary[]> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_elements")
    .select("pm_issues!inner(id, issue_number, title, building_id, status, deleted_at)")
    .eq("anchor_id", anchorId);
  if (error) throw new Error(error.message);

  const out: LinkedIssueSummary[] = [];
  for (const row of data ?? []) {
    const issue = (
      row as { pm_issues: (LinkedIssueSummary & { deleted_at: string | null }) | null }
    ).pm_issues;
    if (!issue || issue.deleted_at) continue;
    const { id, issue_number, title, building_id, status } = issue;
    out.push({ id, issue_number, title, building_id, status });
  }
  out.sort((a, b) => a.issue_number - b.issue_number);
  const ids = out.map((i) => i.id);
  const [assigneeMap, withViewpoint] = await Promise.all([
    hydrateAssignees(ids),
    getIssueIdsWithViewpoint(ids),
  ]);
  for (const issue of out) {
    issue.assignees = assigneeMap.get(issue.id) ?? [];
    issue.hasViewpoint = withViewpoint.has(issue.id);
  }
  await applyDocumentRootTitles(out);
  return out;
}

/**
 * WHA-3621: every PM issue attached to a model — i.e. the `.ifc` document
 * itself is linked (`pm_issue_documents`) OR any of its elements is linked
 * (`pm_issue_elements.ifc_document_id`). De-duplicated across both link types
 * and across the supplied `docIds` (the currently-loaded IFC document ids in
 * the federated viewer). RLS on `pm_issues` scopes visibility; soft-deleted
 * issues are skipped.
 */
export async function getIssuesForModel(docIds: number[]): Promise<LinkedIssueSummary[]> {
  if (docIds.length === 0) return [];

  const issueCols = "id, issue_number, title, building_id, status, deleted_at";
  const [docRes, elemRes] = await Promise.all([
    (supabase as any)
      .from("pm_issue_documents")
      .select(`pm_issues!inner(${issueCols})`)
      .in("document_id", docIds),
    (supabase as any)
      .from("pm_issue_elements")
      .select(`element_guid, ifc_document_id, pm_issues!inner(${issueCols})`)
      .in("ifc_document_id", docIds),
  ]);
  if (docRes.error) throw new Error(docRes.error.message);
  if (elemRes.error) throw new Error(elemRes.error.message);

  type IssueRow = (LinkedIssueSummary & { deleted_at: string | null }) | null;
  const byId = new Map<number, LinkedIssueSummary>();
  const addIssue = (issue: IssueRow) => {
    if (!issue || issue.deleted_at) return;
    if (byId.has(issue.id)) return;
    const { id, issue_number, title, building_id, status } = issue;
    byId.set(id, { id, issue_number, title, building_id, status });
  };

  for (const row of docRes.data ?? []) addIssue((row as { pm_issues: IssueRow }).pm_issues);

  // WHA-3671: collect each issue's linked elements on the loaded models so the
  // model-issue list can offer "Go to element". elemRes is already filtered to
  // docIds, so every guid here is guaranteed to be present in the scene.
  const elemsByIssue = new Map<number, { guid: string; docId: number }[]>();
  for (const row of elemRes.data ?? []) {
    const r = row as {
      element_guid: string | null;
      ifc_document_id: number | null;
      pm_issues: IssueRow;
    };
    addIssue(r.pm_issues);
    if (!r.pm_issues || r.pm_issues.deleted_at) continue;
    if (!r.element_guid || r.ifc_document_id == null) continue;
    const list = elemsByIssue.get(r.pm_issues.id) ?? [];
    if (!list.some((e) => e.guid === r.element_guid)) {
      list.push({ guid: r.element_guid, docId: r.ifc_document_id });
    }
    elemsByIssue.set(r.pm_issues.id, list);
  }

  for (const [id, summary] of byId) summary.focusableElements = elemsByIssue.get(id) ?? [];

  const ids = [...byId.keys()];
  const [assigneeMap, withViewpoint] = await Promise.all([
    hydrateAssignees(ids),
    getIssueIdsWithViewpoint(ids),
  ]);
  for (const [id, summary] of byId) {
    summary.assignees = assigneeMap.get(id) ?? [];
    summary.hasViewpoint = withViewpoint.has(id);
  }

  return [...byId.values()].sort((a, b) => a.issue_number - b.issue_number);
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export async function listComments(issueId: number): Promise<PMIssueComment[]> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_comments")
    .select("*")
    .eq("issue_id", issueId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);
  const comments = (data ?? []) as PMIssueComment[];

  // Hydrate author profiles via a single batched lookup so the comment
  // header can render the real name instead of falling back to "Unknown"
  // (WHA-3313). No FK relationship is declared between pm_issue_comments
  // and user_profiles, so a PostgREST embed isn't an option.
  if (comments.length > 0) {
    const authorIds = Array.from(new Set(comments.map((c) => c.author_id)));
    const { data: users, error: uErr } = await (supabase as any)
      .from("user_profiles")
      .select("id, first_name, last_name, email, avatar_url, color")
      .in("id", authorIds);
    if (uErr) throw new Error(uErr.message);
    const userMap = new Map<number, UserProfile>((users ?? []).map((u: UserProfile) => [u.id, u]));
    for (const c of comments) c.author = userMap.get(c.author_id);

    // WHA-3376: hydrate per-comment document attachments via the
    // pm_issue_comment_documents junction. Same batching shape as the
    // issue-level hydration in listIssues to keep one round-trip per
    // table regardless of comment count.
    const commentIds = comments.map((c) => c.id);
    const { data: linkRows, error: linkErr } = await (supabase as any)
      .from("pm_issue_comment_documents")
      .select("comment_id, document_id")
      .in("comment_id", commentIds);
    if (linkErr) throw new Error(linkErr.message);

    const docIds = Array.from(
      new Set(((linkRows ?? []) as { document_id: number }[]).map((r) => r.document_id))
    );
    if (docIds.length > 0) {
      const { data: docs, error: docErr } = await (supabase as any)
        .from("documents")
        .select(LINKED_DOCUMENT_COLUMNS)
        .in("id", docIds);
      if (docErr) throw new Error(docErr.message);
      const docMap = new Map<number, LinkedDocument>(
        ((docs ?? []) as LinkedDocument[]).map((d) => [d.id, d])
      );
      // WHA-3457: resolve folder-path breadcrumbs for the tooltip.
      await attachDocumentBreadcrumbs(Array.from(docMap.values()));
      const grouped = new Map<number, LinkedDocument[]>();
      for (const r of (linkRows ?? []) as { comment_id: number; document_id: number }[]) {
        const doc = docMap.get(r.document_id);
        if (!doc) continue;
        const list = grouped.get(r.comment_id) ?? [];
        list.push(doc);
        grouped.set(r.comment_id, list);
      }
      for (const c of comments) c.linked_documents = grouped.get(c.id) ?? [];
    } else {
      for (const c of comments) c.linked_documents = [];
    }
  }

  return comments;
}

export async function createComment(
  commentData: {
    issue_id: number;
    author_id: number;
    body: string;
  },
  mentionedUserIds: number[] = [],
  documentIds: number[] = []
): Promise<PMIssueComment> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_comments")
    .insert(commentData)
    .select()
    .single();

  if (error) throw new Error(error.message);
  const comment = data as PMIssueComment;

  // WHA-3376: link any documents the composer staged. Best-effort —
  // failure here logs but does not roll back the comment itself, matching
  // the mention-plumbing approach below.
  if (documentIds.length > 0) {
    try {
      const rows = documentIds.map((documentId) => ({
        comment_id: comment.id,
        document_id: documentId,
      }));
      const { error: docErr } = await (supabase as any)
        .from("pm_issue_comment_documents")
        .insert(rows);
      if (docErr) throw docErr;
    } catch (docErr) {
      console.error("[pmRepo.createComment] document linking failed:", docErr);
    }
  }

  // Best-effort mention plumbing. A failure here should not break commenting:
  // we log the error and continue so the comment still shows in the UI.
  if (mentionedUserIds.length > 0) {
    try {
      const rows = mentionedUserIds.map((userId) => ({
        comment_id: comment.id,
        mentioned_user_id: userId,
      }));
      const { error: mentionErr } = await (supabase as any)
        .from("pm_issue_comment_mentions")
        .insert(rows);
      if (mentionErr) throw mentionErr;
      // Email notification is sent by the pm-digest K8s CronJob
      // (franklin-data-pipeline), still every 15 minutes — it now triggers
      // the send-pm-digest Edge Function (WHA-3868), which reads the
      // mention rows inserted above and dedupes via pm_digest_deliveries.
    } catch (mentionErr) {
      console.error("[pmRepo.createComment] mention plumbing failed:", mentionErr);
    }
  }

  return comment;
}

/**
 * WHA-3453: edit an existing comment's body. Stamps `modified_at` /
 * `modified_by` (the UI shows an "Edited" marker when `modified_at` is set)
 * and resyncs the comment's `@mention` rows so added/removed mentions take
 * effect — mirroring the best-effort mention plumbing in `createComment`.
 */
export async function updateComment(
  commentId: number,
  body: string,
  modifiedBy: number,
  mentionedUserIds: number[] = []
): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issue_comments")
    .update({ body, modified_at: new Date().toISOString(), modified_by: modifiedBy })
    .eq("id", commentId);
  if (error) throw new Error(error.message);

  try {
    const { error: delErr } = await (supabase as any)
      .from("pm_issue_comment_mentions")
      .delete()
      .eq("comment_id", commentId);
    if (delErr) throw delErr;
    if (mentionedUserIds.length > 0) {
      const rows = mentionedUserIds.map((userId) => ({
        comment_id: commentId,
        mentioned_user_id: userId,
      }));
      const { error: insErr } = await (supabase as any)
        .from("pm_issue_comment_mentions")
        .insert(rows);
      if (insErr) throw insErr;
    }
  } catch (mentionErr) {
    console.error("[pmRepo.updateComment] mention resync failed:", mentionErr);
  }
}

// ---------------------------------------------------------------------------
// Disciplines
// ---------------------------------------------------------------------------

export async function listDisciplines(): Promise<Discipline[]> {
  return readDisciplines(null);
}

// ---------------------------------------------------------------------------
// Realtime subscriptions
//
// Each returns the channel HANDLE, and teardown must go through its
// `unsubscribe()` — `supabase.removeChannel()` on the raw channel skips the
// torn latch (so a late SUBSCRIBED still calls back into an unmounted
// consumer) and leaks the topic's entry in the duplicate-topic registry.
// ---------------------------------------------------------------------------

/**
 * Issue rows for a building. `pm_issues` has default replica identity, so a
 * DELETE payload carries only the primary key and an UPDATE's `payload.old` is
 * empty — treat every event as "something changed" and re-read.
 */
export function subscribeToIssues(buildingId: number, callback: () => void): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issues", buildingId),
    bindings: [
      { event: "*", table: "pm_issues", filter: `building_id=eq.${buildingId}`, handler: callback },
    ],
    onResync: callback,
  });
}

/**
 * Dependency edges, from anyone — the chatbot, a colleague, a cascade. The
 * table carries no building column to filter on, so every edge the user may
 * read arrives and the caller re-reads its own building; edges are rare
 * writes, so the unfiltered stream is small. Same payload caveat as above.
 */
export function subscribeToIssueDependencies(callback: () => void): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_dependencies"),
    bindings: [{ event: "*", table: "pm_issue_dependencies", handler: callback }],
    onResync: callback,
  });
}

/**
 * Checklist items anywhere in a building, for the progress badges on the list
 * and the board. Same payload caveat as above.
 */
export function subscribeToBuildingChecklists(
  buildingId: number,
  callback: () => void
): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_checklist_items", buildingId),
    bindings: [
      {
        event: "*",
        table: "pm_issue_checklist_items",
        filter: `building_id=eq.${buildingId}`,
        handler: callback,
      },
    ],
    onResync: callback,
  });
}

/** The checklist of one open issue. Same payload caveat as above. */
export function subscribeToIssueChecklist(
  issueId: number,
  callback: () => void
): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_checklist_items", "issue", issueId),
    bindings: [
      {
        event: "*",
        table: "pm_issue_checklist_items",
        filter: `issue_id=eq.${issueId}`,
        handler: callback,
      },
    ],
    onResync: callback,
  });
}

/** The comment thread of one issue. Same payload caveat as above. */
export function subscribeToComments(issueId: number, callback: () => void): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_comments", issueId),
    bindings: [
      {
        event: "*",
        table: "pm_issue_comments",
        filter: `issue_id=eq.${issueId}`,
        handler: callback,
      },
    ],
    onResync: callback,
  });
}

/**
 * Issue activity for a building — what the Notifications and Mentions nav dots
 * and the activity feed are counting.
 *
 * Trigger-written, and the triggers log a remove+add for an assignee that did
 * not actually change (see `syncAssignees`), so one issue save can emit several
 * rows. Callers must coalesce.
 */
export function subscribeToActivity(buildingId: number, callback: () => void): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_activity", buildingId),
    bindings: [
      {
        event: "*",
        table: "pm_issue_activity",
        filter: `building_id=eq.${buildingId}`,
        handler: callback,
      },
    ],
    onResync: callback,
  });
}

// ---------------------------------------------------------------------------
// Custom issue statuses (pm_issue_statuses)
// ---------------------------------------------------------------------------

/**
 * Flush the status catalogue after any write to it.
 *
 * Returns the refetch, so a caller that hands the new key straight to a
 * picker can await it. Radix's Select paints nothing for a value no mounted
 * item carries, so selecting a status the catalogue has not caught up with
 * left the New Issue form's status field blank until the user picked again.
 */
function invalidateStatusCatalogue(): Promise<void> {
  invalidateIssueCountCaches();
  return queryClient.invalidateQueries({ queryKey: ["pm-issue-statuses"] });
}

/**
 * Every custom status defined by any project of one building.
 *
 * Statuses are scoped to a building PROJECT, but the issues page is scoped to
 * a building and can show several projects at once, so fetching per project
 * would need one request per project and would still miss a row for an issue
 * whose project is filtered out. The inner embed filters by the parent's
 * building without returning the parent's columns.
 */
export async function listIssueStatuses(buildingId: number): Promise<PMIssueStatus[]> {
  const { data, error } = await supabase
    .from("pm_issue_statuses")
    .select(
      "id, building_project_id, key, label_en, label_et, category, dot_color, bg_color, text_color, sort_order, building_projects!inner(building_id)"
    )
    .eq("building_projects.building_id", buildingId)
    .order("sort_order", { ascending: true })
    .order("id", { ascending: true });

  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as PMIssueStatus[]).map((row) => ({
    id: row.id,
    building_project_id: row.building_project_id,
    key: row.key,
    label_en: row.label_en,
    label_et: row.label_et,
    category: row.category,
    dot_color: row.dot_color,
    bg_color: row.bg_color,
    text_color: row.text_color,
    sort_order: row.sort_order,
  }));
}

export interface NewIssueStatus {
  building_project_id: number;
  key: string;
  label_en: string;
  label_et?: string | null;
  category?: IssueStatusCategory;
  dot_color: string;
  bg_color: string;
  text_color: string;
  sort_order?: number;
}

export async function createIssueStatus(input: NewIssueStatus): Promise<PMIssueStatus> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_statuses")
    .insert(input)
    .select()
    .single();
  if (error) throw new Error(error.message);
  await invalidateStatusCatalogue();
  return data as PMIssueStatus;
}

export async function updateIssueStatus(
  id: number,
  patch: Partial<Omit<PMIssueStatus, "id" | "building_project_id" | "key">>
): Promise<void> {
  const { error } = await (supabase as any).from("pm_issue_statuses").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
  await invalidateStatusCatalogue();
}

/**
 * Delete a status. The database refuses while any live issue still holds the
 * key, so callers should offer to reassign first; `friendlyIssueError` turns
 * the refusal into readable text if they do not.
 */
export async function deleteIssueStatus(id: number): Promise<void> {
  const { error } = await (supabase as any).from("pm_issue_statuses").delete().eq("id", id);
  if (error) throw new Error(error.message);
  await invalidateStatusCatalogue();
}

/** How many live issues hold this status — what the delete affordance shows. */
export async function countIssuesWithStatus(
  buildingProjectId: number,
  status: IssueStatus
): Promise<number> {
  const { count, error } = await supabase
    .from("pm_issues")
    .select("id", { count: "exact", head: true })
    .eq("building_project_id", buildingProjectId)
    .eq("status", status)
    .is("deleted_at", null);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

// ---------------------------------------------------------------------------
// Issue dependencies (pm_issue_dependencies)
// ---------------------------------------------------------------------------

/**
 * Every dependency among the issues of one building.
 *
 * Fetched for the whole building rather than per issue: the Gantt draws every
 * arrow at once, and one request beats one per bar. Edges whose other end the
 * caller cannot read are filtered out by RLS, which checks BOTH ends.
 */
export async function listIssueDependencies(buildingId: number): Promise<PMIssueDependency[]> {
  const { data, error } = await supabase
    .from("pm_issue_dependencies")
    .select(
      "id, predecessor_issue_id, successor_issue_id, dep_type, lag_days, hardness, pm_issues!pm_issue_dependencies_successor_fk!inner(building_id)"
    )
    .eq("pm_issues.building_id", buildingId);

  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as PMIssueDependency[]).map((row) => ({
    id: row.id,
    predecessor_issue_id: row.predecessor_issue_id,
    successor_issue_id: row.successor_issue_id,
    dep_type: row.dep_type,
    lag_days: row.lag_days,
    hardness: row.hardness,
  }));
}

export async function createIssueDependency(input: {
  predecessor_issue_id: number;
  successor_issue_id: number;
  dep_type?: IssueDependencyType;
  lag_days?: number;
  hardness?: IssueDependencyHardness;
}): Promise<PMIssueDependency> {
  const { data, error } = await (supabase as any)
    .from("pm_issue_dependencies")
    .insert(input)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as PMIssueDependency;
}

export async function updateIssueDependency(
  id: number,
  patch: Partial<Pick<PMIssueDependency, "dep_type" | "lag_days" | "hardness">>
): Promise<void> {
  const { error } = await (supabase as any)
    .from("pm_issue_dependencies")
    .update(patch)
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deleteIssueDependency(id: number): Promise<void> {
  const { error } = await (supabase as any).from("pm_issue_dependencies").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// The schedule cascade
//
// Both calls go to Postgres rather than being computed here. The engine has to
// give the chatbot the same answer it gives this app, and the page holds at
// most PM_PAGE_SIZE_GANTT issues — a cascade computed from the loaded rows
// would stop at the page boundary and under-report what moves.
// ---------------------------------------------------------------------------

/**
 * `start` and `due` are the COMPLETE new pair, not a patch: pass the issue's
 * current value for whichever end is not changing. In a patch shape, null
 * would mean both "leave alone" and "clear this date".
 */
export async function previewScheduleChange(
  issueId: number,
  start: string | null,
  due: string | null
): Promise<SchedulePreview> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_preview_schedule_change",
    { p_issue_id: issueId, p_start: start, p_due: due }
  );
  if (error) throw new Error(error.message);
  return data as unknown as SchedulePreview;
}

/**
 * Apply the change. `cascade` false is "Keep current dates" — it still writes
 * the edit the user just made, and only declines to move the dependents.
 * `pin` marks the issue user-scheduled so a later predecessor move warns
 * rather than overwriting it; cascade-driven writes never set it.
 */
export async function applyScheduleChange(
  issueId: number,
  start: string | null,
  due: string | null,
  cascade: boolean,
  pin = true
): Promise<SchedulePreview> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_apply_schedule_change",
    { p_issue_id: issueId, p_start: start, p_due: due, p_cascade: cascade, p_pin: pin }
  );
  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
  return data as unknown as SchedulePreview;
}

/**
 * Preview a whole staged arrangement: every date change at once, with
 * dependency changes that have not been written overlaid on the graph.
 *
 * Deliberately not N calls to previewScheduleChange — each of those relaxes the
 * project around its own single target and would undo the others' work.
 */
export async function previewScheduleBatch(
  buildingProjectId: number,
  dateChanges: ScheduleDateChange[],
  edgeChanges: ScheduleEdgeChanges | null
): Promise<SchedulePreview> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_preview_schedule_batch",
    {
      p_building_project_id: buildingProjectId,
      p_date_changes: dateChanges,
      p_edge_changes: edgeChanges,
    }
  );
  if (error) throw new Error(error.message);
  return data as unknown as SchedulePreview;
}

/**
 * Commit a staged arrangement. Dependencies, dates and the cascade they imply
 * all land in one transaction, so a schedule is never half saved.
 */
export async function applyScheduleBatch(
  buildingProjectId: number,
  dateChanges: ScheduleDateChange[],
  edgeChanges: ScheduleEdgeChanges | null,
  cascade: boolean
): Promise<SchedulePreview> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_apply_schedule_batch",
    {
      p_building_project_id: buildingProjectId,
      p_date_changes: dateChanges,
      p_edge_changes: edgeChanges,
      p_cascade: cascade,
    }
  );
  if (error) throw new Error(error.message);
  invalidateIssueCountCaches();
  queryClient.invalidateQueries({ queryKey: ["pm-issue-dependencies"] });
  return data as unknown as SchedulePreview;
}

// ---------------------------------------------------------------------------
// Issue placements (pm_issue_placements)
//
// The shared top-to-bottom arrangement of the board and the Gantt. The rules
// for turning these rows into an order live in domains/pm/utils/issuePlacement.
// ---------------------------------------------------------------------------

const placementTable = () => (supabase as unknown as SupabaseClient).from("pm_issue_placements");

/** Every placement one building has for a layout and grouping. RLS drops rows
 *  for issues the caller cannot see. */
export async function listIssuePlacements(
  buildingId: number,
  layout: PlacementLayout,
  groupBy: string
): Promise<IssuePlacementRow[]> {
  const { data, error } = await placementTable()
    .select("issue_id, layout, group_by, group_key, position")
    .eq("building_id", buildingId)
    .eq("layout", layout)
    .eq("group_by", groupBy);
  if (error) throw new Error(error.message);
  return (data ?? []) as IssuePlacementRow[];
}

/**
 * Write positions for one band. An upsert on the natural key, so rows for
 * issues arranged here before are moved rather than duplicated. `building_id`
 * is left out: the trigger stamps it from the issue before NOT NULL is checked.
 */
export async function saveIssuePlacements(
  layout: PlacementLayout,
  groupBy: string,
  groupKey: string,
  positions: { issue_id: number; position: number }[]
): Promise<void> {
  if (positions.length === 0) return;
  const { error } = await placementTable().upsert(
    positions.map((row) => ({
      ...row,
      layout,
      group_by: groupBy,
      group_key: groupKey,
    })),
    { onConflict: "issue_id,layout,group_by,group_key" }
  );
  if (error) throw new Error(error.message);
}

/**
 * Drop the rows an issue left behind in bands it is no longer in. Grouped by
 * band because the natural key is composite — one statement per band, and a
 * move touches one.
 */
export async function deleteIssuePlacements(
  layout: PlacementLayout,
  groupBy: string,
  rows: readonly { issue_id: number; group_key: string }[]
): Promise<void> {
  const byBand = new Map<string, number[]>();
  for (const row of rows) {
    const ids = byBand.get(row.group_key);
    if (ids) ids.push(row.issue_id);
    else byBand.set(row.group_key, [row.issue_id]);
  }
  for (const [groupKey, issueIds] of byBand) {
    const { error } = await placementTable()
      .delete()
      .eq("layout", layout)
      .eq("group_by", groupBy)
      .eq("group_key", groupKey)
      .in("issue_id", issueIds);
    if (error) throw new Error(error.message);
  }
}

/** Placement changes for a building, from anyone. Same payload caveat as
 *  `subscribeToIssues`: treat an event as "re-read". */
export function subscribeToIssuePlacements(
  buildingId: number,
  callback: () => void
): ResyncChannelHandle {
  return createResyncChannel({
    topic: realtimeTopic("pm_issue_placements", buildingId),
    bindings: [
      {
        event: "*",
        table: "pm_issue_placements",
        filter: `building_id=eq.${buildingId}`,
        handler: callback,
      },
    ],
    onResync: callback,
  });
}

// ---------------------------------------------------------------------------
// Schedule snapshots (pm_schedule_snapshots)
// ---------------------------------------------------------------------------

/** Everything but the payload — the history list never needs the blob. */
const SNAPSHOT_LIST_COLUMNS =
  "id, building_project_id, label, schema_version, issue_count, dependency_count, completion_date, created_at, created_by";

export async function listScheduleSnapshots(
  buildingProjectId: number
): Promise<ScheduleSnapshot[]> {
  const { data, error } = await supabase
    .from("pm_schedule_snapshots")
    .select(SNAPSHOT_LIST_COLUMNS)
    .eq("building_project_id", buildingProjectId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ScheduleSnapshot[];
}

/** The payload, fetched only when a snapshot is actually opened. */
export async function getScheduleSnapshot(id: number): Promise<ScheduleSnapshot> {
  const { data, error } = await supabase
    .from("pm_schedule_snapshots")
    .select(`${SNAPSHOT_LIST_COLUMNS}, payload`)
    .eq("id", id)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as ScheduleSnapshot;
}

/**
 * Capture the project's schedule. Server-side: the Gantt holds at most
 * PM_PAGE_SIZE_GANTT issues, and a snapshot of part of a project would be a
 * lie about the project.
 */
export async function saveScheduleSnapshot(
  buildingProjectId: number,
  label: string | null
): Promise<ScheduleSnapshot> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_save_schedule_snapshot",
    { p_building_project_id: buildingProjectId, p_label: label }
  );
  if (error) throw new Error(error.message);
  queryClient.invalidateQueries({ queryKey: ["pm-schedule-snapshots", buildingProjectId] });
  return data as unknown as ScheduleSnapshot;
}

/** Thrown by `renameScheduleSnapshot` before pipeline #373's RPC exists. */
export class SnapshotRenameUnavailableError extends Error {
  constructor() {
    super("pm_rename_schedule_snapshot is not deployed");
    this.name = "SnapshotRenameUnavailableError";
  }
}

/**
 * Rename a snapshot — the label only; the capture itself is immutable, which
 * is why this is an RPC and the table has no UPDATE policy. Blank clears it.
 * Awaits the list's refetch, so the row reads the new name as the input closes.
 */
export async function renameScheduleSnapshot(
  id: number,
  buildingProjectId: number,
  label: string | null
): Promise<void> {
  const { error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_rename_schedule_snapshot",
    { p_snapshot_id: id, p_label: label }
  );
  // PGRST202: PostgREST has no such function; 42883: Postgres has none.
  if (error?.code === "PGRST202" || error?.code === "42883") {
    throw new SnapshotRenameUnavailableError();
  }
  if (error) throw new Error(error.message);
  await queryClient.invalidateQueries({ queryKey: ["pm-schedule-snapshots", buildingProjectId] });
}

/** A delete RLS refused: PostgREST answers that with no error, just 0 rows. */
export class SnapshotDeleteRefusedError extends Error {
  constructor() {
    super("pm_schedule_snapshots delete matched no rows");
    this.name = "SnapshotDeleteRefusedError";
  }
}

export async function deleteScheduleSnapshot(id: number, buildingProjectId: number): Promise<void> {
  // Counted, not read back: a RETURNING read-back also comes back empty for a
  // delete that went through but whose row the caller may not SELECT.
  const { count, error } = await (supabase as unknown as SupabaseClient)
    .from("pm_schedule_snapshots")
    .delete({ count: "exact" })
    .eq("id", id);
  // Whatever happened, the list re-reads: a snapshot already deleted elsewhere
  // must leave it even when this delete matched nothing.
  await queryClient.invalidateQueries({ queryKey: ["pm-schedule-snapshots", buildingProjectId] });
  if (error) throw new Error(error.message);
  // 0 is a refusal (or a row already gone). No count means no Content-Range,
  // which cannot confirm a delete — and a destructive action is not reported
  // as done unconfirmed. The list has just re-read either way, so it shows
  // what is true. (PostgREST does send the header for count=exact.)
  if (!count) throw new SnapshotDeleteRefusedError();
}

// ---------------------------------------------------------------------------
// Issue checklists (pm_issue_checklist_items)
//
// Rights are the issue's: VIEW_PM reads, MANAGE_PM writes — ticking included.
// `done_at` / `done_by`, authorship and `building_id` are stamped by the
// server, so none of them is ever sent.
// ---------------------------------------------------------------------------

const checklistTable = () =>
  (supabase as unknown as SupabaseClient).from("pm_issue_checklist_items");

const CHECKLIST_COLUMNS = "id, issue_id, text, position, is_done, done_at, done_by";

/** The longest item the database accepts (`pm_issue_checklist_items_text_length`). */
export const CHECKLIST_ITEM_MAX_LENGTH = 500;

/**
 * One issue's checklist, in order. `null` when the table is not deployed yet —
 * the panel hides the section rather than reporting an error.
 */
export async function listChecklistItems(issueId: number): Promise<PMIssueChecklistItem[] | null> {
  const { data, error } = await checklistTable()
    .select(CHECKLIST_COLUMNS)
    .eq("issue_id", issueId)
    .order("position", { ascending: true })
    .order("id", { ascending: true });
  if (error) {
    if (isMissingRelation(error)) return null;
    throw pgError(error);
  }
  return (data ?? []) as PMIssueChecklistItem[];
}

/** Append items, in the order given, from `startPosition` on. */
export async function createChecklistItems(
  issueId: number,
  texts: string[],
  startPosition: number
): Promise<PMIssueChecklistItem[]> {
  if (texts.length === 0) return [];
  const { data, error } = await checklistTable()
    .insert(texts.map((text, i) => ({ issue_id: issueId, text, position: startPosition + i })))
    .select(CHECKLIST_COLUMNS);
  if (error) throw pgError(error);
  return (data ?? []) as PMIssueChecklistItem[];
}

/**
 * Rename or tick one item. An UPDATE the policies filter out reports success
 * having touched nothing, so zero rows back is thrown as PGRST116 — the item
 * is gone, or no longer ours to change — instead of being shown as saved.
 */
export async function updateChecklistItem(
  id: number,
  patch: { text?: string; is_done?: boolean }
): Promise<PMIssueChecklistItem> {
  const { data, error } = await checklistTable()
    .update(patch)
    .eq("id", id)
    .select(CHECKLIST_COLUMNS);
  if (error) throw pgError(error);
  const row = (data ?? [])[0] as PMIssueChecklistItem | undefined;
  if (!row) throw pgError({ message: "Checklist item not found", code: "PGRST116" });
  return row;
}

/** Delete one item. Already gone counts as done. */
export async function deleteChecklistItem(id: number): Promise<void> {
  const { error } = await checklistTable().delete().eq("id", id);
  if (error) throw pgError(error);
}

/**
 * Store a new order. `itemIds` must be exactly the issue's current items; a
 * list that gained or lost one since it was read is refused with the hint
 * `pm_checklist_order_stale` (see {@link isStaleChecklistOrder}).
 */
export async function reorderChecklistItems(issueId: number, itemIds: number[]): Promise<void> {
  const { error } = await (supabase as unknown as SupabaseClient).rpc(
    "pm_reorder_checklist_items",
    { p_issue_id: issueId, p_item_ids: itemIds }
  );
  if (error) throw pgError(error);
}

export function isStaleChecklistOrder(e: unknown): boolean {
  return (e as { hint?: string } | null)?.hint === "pm_checklist_order_stale";
}
